import { useEffect, useState } from "react";
import { Scissors, LogOut, CalendarCheck, RefreshCw, Trash2, Bell, Clock } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { openExternal, waIntent } from "@/constants/links";
import { PRODUCTS } from "./ProductsPage";
import { isTenant, tenantSlug, setTenantToken, clearTenantToken } from "@/tenant";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TENANT_MODE = isTenant();

const FEATURE_ITEMS = [
  { key: "chat", label: "Jordan — assistente prenotazioni" },
  { key: "diretta", label: "Prenotazione rapida (senza chat)" },
  { key: "sumup", label: "Prenota da solo (SumUp)" },
  { key: "recensioni", label: "Pulsante recensioni" },
  { key: "whatsapp", label: "Pulsante WhatsApp" },
  { key: "shop", label: "Prodotti & nuvola Shop" },
  { key: "ai_photo", label: "Prova il taglio con l'IA ✨" },
];

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const TIME_SLOTS = (() => {
  const out = [];
  for (const [from, to] of [[9 * 60, 13 * 60 + 30], [15 * 60 + 30, 20 * 60 + 30]]) {
    for (let m = from; m + 30 <= to; m += 30) {
      out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    }
  }
  return out;
})();
const IT_GIORNI = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
const IT_MESI = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];
const toGiorno = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  return `${IT_GIORNI[d.getDay()]} ${d.getDate()} ${IT_MESI[d.getMonth()]}`;
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [cal, setCal] = useState(null);
  const [calError, setCalError] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({
    nome: "",
    telefono: "",
    servizio: "",
    barbiere: "",
    data: "",
    ora: "",
  });
  const [manualError, setManualError] = useState("");
  const [manualSaving, setManualSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [svcEdits, setSvcEdits] = useState({});
  const [newSvc, setNewSvc] = useState({ nome: "", prezzo: "", durata: "30" });
  const [svcError, setSvcError] = useState("");

  const api = async (path, opts = {}, retry = true) => {
    const res = await fetch(`${BACKEND}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...opts,
    });
    if (res.status === 401 && retry) {
      const r = await fetch(`${BACKEND}/api/auth/refresh`, { method: "POST", credentials: "include" });
      if (r.ok) return api(path, opts, false);
    }
    return res;
  };

  const apiUpload = async (path, formData, retry = true) => {
    const res = await fetch(`${BACKEND}${path}`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (res.status === 401 && retry) {
      const r = await fetch(`${BACKEND}/api/auth/refresh`, { method: "POST", credentials: "include" });
      if (r.ok) return apiUpload(path, formData, false);
    }
    return res;
  };

  const loadBookings = async () => {
    setLoadingList(true);
    const res = await api("/api/bookings");
    if (res.ok) setBookings(await res.json());
    setLoadingList(false);
  };

  const saveManual = async () => {
    if (manualSaving) return;
    setManualError("");
    if (!manual.nome.trim() || !manual.data || !manual.ora || !manual.servizio) {
      setManualError("Completa nome, servizio, giorno e ora.");
      return;
    }
    setManualSaving(true);
    const res = await api("/api/bookings", {
      method: "POST",
      body: JSON.stringify({
        nome: manual.nome,
        telefono: manual.telefono,
        servizio: manual.servizio,
        barbiere: manual.barbiere,
        giorno: toGiorno(manual.data),
        ora: manual.ora,
      }),
    });
    if (res.ok) {
      setManual({ nome: "", telefono: "", servizio: "", barbiere: "", data: "", ora: "" });
      setShowManual(false);
      await loadBookings();
    } else {
      const d = await res.json().catch(() => ({}));
      setManualError(typeof d.detail === "string" ? d.detail : "Errore nel salvataggio");
    }
    setManualSaving(false);
  };

  const [media, setMedia] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const readError = async (res) => {
    try {
      const d = await res.json();
      return d.detail || `Errore di caricamento (${res.status})`;
    } catch {
      return `Errore di rete (${res.status})`;
    }
  };

  const loadMedia = async () => {
    const res = await api("/api/media");
    if (res.ok) setMedia(await res.json());
  };

  const uploadMedia = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setUploadError("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("autore", autore);
    try {
      const res = await fetch(`${BACKEND}/api/media`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (res.ok) {
        setFile(null);
        setAutore("");
        await loadMedia();
      } else {
        setUploadError(await readError(res));
      }
    } catch {
      setUploadError("Errore di rete: controlla la connessione e riprova.");
    }
    setUploading(false);
  };

  const removeMedia = async (id) => {
    const res = await api(`/api/media/${id}`, { method: "DELETE" });
    if (res.ok) setMedia((list) => list.filter((x) => x.id !== id));
  };

  const [autore, setAutore] = useState("");

  const [team, setTeam] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [staffAccess, setStaffAccess] = useState([]);
  const [newAccessName, setNewAccessName] = useState("");
  const [copiedAccess, setCopiedAccess] = useState(null);
  const [teamStatus, setTeamStatus] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [features, setFeatures] = useState({
    chat: true, sumup: true, recensioni: true, whatsapp: true, galleria: true, shop: true, promo: true, diretta: true, ai_photo: true,
  });
  const [featOpen, setFeatOpen] = useState(() => localStorage.getItem("admin_feat_open") === "1");
  const [openRev, setOpenRev] = useState(() => localStorage.getItem("admin_rev_open") === "1");
  const [openTen, setOpenTen] = useState(() => localStorage.getItem("admin_ten_open") === "1");
  const [openBc, setOpenBc] = useState(() => localStorage.getItem("admin_bc_open") === "1");
  const [openAg, setOpenAg] = useState(() => localStorage.getItem("admin_ag_open") === "1");
  const [agendaShare, setAgendaShare] = useState({ token: "", pin: "", phone: "" });
  const [socioPhone, setSocioPhone] = useState("");
  const [socioContact, setSocioContact] = useState({ name: "", phone: "" });
  const [openSocio, setOpenSocio] = useState(() => localStorage.getItem("admin_socio_open") === "1");
  const [socioSaved, setSocioSaved] = useState(false);
  const [hours, setHours] = useState({ morning_open: "09:00", morning_close: "13:30", evening_open: "15:30", evening_close: "20:30" });
  const [openHours, setOpenHours] = useState(() => localStorage.getItem("admin_hours_open") === "1");
  const [hoursSaved, setHoursSaved] = useState(false);

  const loadHours = async () => {
    const res = await api("/api/settings/hours");
    if (res.ok) {
      const d = await res.json();
      setHours({
        morning_open: d.morning_open, morning_close: d.morning_close,
        evening_open: d.evening_open, evening_close: d.evening_close,
      });
    }
  };

  const saveHours = async () => {
    const res = await api("/api/settings/hours", { method: "POST", body: JSON.stringify(hours) });
    if (res.ok) {
      setHoursSaved(true);
      setTimeout(() => setHoursSaved(false), 2000);
    }
  };

  const loadSocioContact = async () => {
    const res = await api("/api/settings/socio-contact");
    if (res.ok) setSocioContact(await res.json());
  };

  const saveSocioContact = async () => {
    const res = await api("/api/settings/socio-contact", {
      method: "POST",
      body: JSON.stringify(socioContact),
    });
    if (res.ok) {
      setSocioSaved(true);
      setTimeout(() => setSocioSaved(false), 2000);
    }
  };
  const [weekSummary, setWeekSummary] = useState(null);
  const [openWeek, setOpenWeek] = useState(() => localStorage.getItem("admin_week_open") !== "0");
  const [weekTab, setWeekTab] = useState("settimana"); // "settimana" | "mese"
  const [inventory, setInventory] = useState([]);
  const [newItem, setNewItem] = useState({ nome: "", note: "" });
  const [openInv, setOpenInv] = useState(() => localStorage.getItem("admin_inv_open") === "1");

  const loadInventory = async () => {
    const res = await api("/api/inventory");
    if (res.ok) setInventory(await res.json());
  };

  const addItem = async () => {
    if (!newItem.nome.trim()) return;
    const res = await api("/api/inventory", { method: "POST", body: JSON.stringify(newItem) });
    if (res.ok) {
      setNewItem({ nome: "", note: "" });
      await loadInventory();
    }
  };

  const toggleItem = async (id) => {
    await api(`/api/inventory/${id}/toggle`, { method: "PATCH" });
    await loadInventory();
  };

  const removeItem = async (id) => {
    await api(`/api/inventory/${id}`, { method: "DELETE" });
    await loadInventory();
  };

  const shareInventory = () => {
    const pending = inventory.filter((it) => !it.comprato);
    if (!pending.length) return;
    const lines = pending.map((it) => `• ${it.nome}${it.note ? ` (${it.note})` : ""}`);
    const text = ["🛒 Da comprare per il salone:", ...lines].join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const loadWeekSummary = async () => {
    const res = await api("/api/stats/week-summary");
    if (res.ok) setWeekSummary(await res.json());
  };

  const togglePanel = (key, val, setter) => {
    setter(val);
    localStorage.setItem(key, val ? "1" : "0");
  };

  const loadAgendaShare = async () => {
    const res = await api("/api/agenda-share", { method: "POST", body: JSON.stringify({}) });
    if (res.ok) {
      const d = await res.json();
      setAgendaShare(d);
      if (d.phone) setSocioPhone(d.phone);
    }
  };

  const saveSocioPhone = async (phone) => {
    const res = await api("/api/agenda-share", {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
    if (res.ok) setAgendaShare(await res.json());
  };

  const rotateAgendaShare = async () => {
    if (!window.confirm("Il link vecchio smetterà di funzionare. Continuare?")) return;
    const res = await api("/api/agenda-share/rotate", { method: "POST" });
    if (res.ok) {
      const d = await res.json();
      setAgendaShare((s) => ({ ...s, token: d.token, pin: "" }));
      await loadAgendaShare();
    }
  };

  const agendaUrl = `${window.location.origin}/agenda/${agendaShare.token}`;
  const agendaManageUrl = `${agendaUrl}?pin=${agendaShare.pin}`;

  const sendAgendaWhatsApp = () => {
    const phone = (socioPhone || "").replace(/\D/g, "");
    if (!phone) return;
    saveSocioPhone(phone);
    const text = `Ciao! Ecco l'agenda aggiornata in tempo reale (prossimo mese): ${agendaUrl}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };
  const [blockedNumbers, setBlockedNumbers] = useState([]);
  const [blockedOpen, setBlockedOpen] = useState(() => localStorage.getItem("admin_blocked_open") === "1");
  const [closedDays, setClosedDays] = useState([]);
  const [closedDate, setClosedDate] = useState("");
  const [closedNote, setClosedNote] = useState("");
  const [breaks, setBreaks] = useState([]);
  const [brk, setBrk] = useState({ date: "", inizio: "", fine: "", barbiere: "" });
  const [prodLinks, setProdLinks] = useState({});
  const [prodLinkSel, setProdLinkSel] = useState("");
  const [prodLinkUrl, setProdLinkUrl] = useState("");
  const [prodLinkMsg, setProdLinkMsg] = useState("");
  const [affOpen, setAffOpen] = useState(() => localStorage.getItem("admin_aff_open") === "1");
  const [raffle, setRaffle] = useState({ active: false, title: "", price: "", prizes: [] });
  const [raffleEntries, setRaffleEntries] = useState([]);
  const [rafflePrizeName, setRafflePrizeName] = useState("");
  const [raffleUploading, setRaffleUploading] = useState(false);
  const [newBlocked, setNewBlocked] = useState("");

  const loadTeam = async () => {
    const res = await api("/api/team");
    if (res.ok) setTeam(await res.json());
  };

  // Editor de horarios/días de descanso por barbero
  const [barberHoursEdit, setBarberHoursEdit] = useState({});
  const minToHm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const DAY_CHIPS = ["L", "M", "M", "G", "V", "S", "D"]; // lun-dom

  const toggleBarberHours = (t) => {
    setBarberHoursEdit((p) => {
      if (p[t.id]) {
        const n = { ...p };
        delete n[t.id];
        return n;
      }
      const ch = t.custom_hours;
      return {
        ...p,
        [t.id]: {
          custom: !!ch,
          mo: ch ? minToHm(ch.morning_open) : hours.morning_open,
          mc: ch ? minToHm(ch.morning_close) : hours.morning_close,
          eo: ch ? minToHm(ch.evening_open) : hours.evening_open,
          ec: ch ? minToHm(ch.evening_close) : hours.evening_close,
          days_off: t.days_off || [],
        },
      };
    });
  };

  const saveBarberHours = async (tid) => {
    const e = barberHoursEdit[tid];
    if (!e) return;
    const body = {
      custom_hours: e.custom
        ? { morning_open: e.mo, morning_close: e.mc, evening_open: e.eo, evening_close: e.ec }
        : null,
      days_off: e.days_off,
    };
    const res = await api(`/api/team/${tid}/hours`, { method: "PUT", body: JSON.stringify(body) });
    if (res.ok) {
      setBarberHoursEdit((p) => {
        const n = { ...p };
        delete n[tid];
        return n;
      });
      loadTeam();
    }
  };

  const loadStaffAccess = async () => {
    const res = await api("/api/staff-access");
    if (res.ok) setStaffAccess(await res.json());
  };

  const loadTeamStatus = async () => {
    const res = await api("/api/staff-status");
    if (res.ok) setTeamStatus(await res.json());
  };

  const loadStats = async () => {
    const res = await api("/api/stats/weekly");
    if (res.ok) setWeeklyStats(await res.json());
    const res2 = await api("/api/stats/clients");
    if (res2.ok) setClientStats(await res2.json());
  };

  const createAccess = async () => {
    if (!newAccessName.trim()) return;
    const res = await api("/api/staff-access", {
      method: "POST",
      body: JSON.stringify({ nome: newAccessName }),
    });
    if (res.ok) {
      setNewAccessName("");
      await loadStaffAccess();
    }
  };

  const toggleAccess = async (a) => {
    const res = await api(`/api/staff-access/${a.id}`, {
      method: "PATCH",
      body: JSON.stringify({ active: !a.active }),
    });
    if (res.ok) await loadStaffAccess();
  };

  const deleteAccess = async (a) => {
    const res = await api(`/api/staff-access/${a.id}`, { method: "DELETE" });
    if (res.ok) await loadStaffAccess();
  };

  const copyAccessLink = async (a) => {
    const link = `${window.location.origin}/staff/${a.token}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt("Copia il link:", link);
    }
    setCopiedAccess(a.id);
    setTimeout(() => setCopiedAccess(null), 2000);
  };

  const resetAccessDevice = async (a) => {
    const res = await api(`/api/staff-access/${a.id}/reset-device`, { method: "POST" });
    if (res.ok) await loadStaffAccess();
  };

  const loadBlocked = async () => {
    const res = await api("/api/blocked-numbers");
    if (res.ok) setBlockedNumbers(await res.json());
  };

  const loadFeatures = async () => {
    const res = await api("/api/features");
    if (res.ok) setFeatures(await res.json());
  };

  const toggleFeature = async (key) => {
    const next = { ...features, [key]: !features[key] };
    setFeatures(next);
    await api("/api/features", {
      method: "POST",
      body: JSON.stringify({ features: next }),
    });
  };

  const addBlocked = async () => {
    if (!newBlocked.trim()) return;
    const res = await api("/api/blocked-numbers", {
      method: "POST",
      body: JSON.stringify({ telefono: newBlocked }),
    });
    if (res.ok) {
      setNewBlocked("");
      await loadBlocked();
    }
  };

  const delBlocked = async (id) => {
    const res = await api(`/api/blocked-numbers/${id}`, { method: "DELETE" });
    if (res.ok) await loadBlocked();
  };

  const loadClosedDays = async () => {
    const res = await api("/api/closed-days");
    if (res.ok) setClosedDays(await res.json());
  };

  const addClosedDay = async () => {
    if (!closedDate) return;
    const res = await api("/api/closed-days", {
      method: "POST",
      body: JSON.stringify({ date: closedDate, note: closedNote }),
    });
    if (res.ok) {
      setClosedDate("");
      setClosedNote("");
      await loadClosedDays();
    }
  };

  const delClosedDay = async (id) => {
    const res = await api(`/api/closed-days/${id}`, { method: "DELETE" });
    if (res.ok) await loadClosedDays();
  };

  const loadBreaks = async () => {
    const res = await api("/api/breaks");
    if (res.ok) setBreaks(await res.json());
  };

  const addBreak = async () => {
    if (!brk.date || !brk.inizio || !brk.fine) return;
    const res = await api("/api/breaks", {
      method: "POST",
      body: JSON.stringify({
        date: brk.date,
        ora_inizio: brk.inizio,
        ora_fine: brk.fine,
        barbiere: brk.barbiere,
      }),
    });
    if (res.ok) {
      setBrk({ date: "", inizio: "", fine: "", barbiere: "" });
      await loadBreaks();
    }
  };

  const delBreak = async (id) => {
    const res = await api(`/api/breaks/${id}`, { method: "DELETE" });
    if (res.ok) await loadBreaks();
  };

  const loadProdLinks = async () => {
    const res = await api("/api/product-links");
    if (res.ok) setProdLinks(await res.json());
  };

  const saveProdLink = async () => {
    if (!prodLinkSel.trim() || !prodLinkUrl.trim()) return;
    const res = await api("/api/product-links", {
      method: "POST",
      body: JSON.stringify({ key: prodLinkSel.trim(), url: prodLinkUrl.trim() }),
    });
    if (res.ok) {
      setProdLinkUrl("");
      setProdLinkSel("");
      setProdLinkMsg("ok");
      await loadProdLinks();
    } else {
      const err = await res.json().catch(() => ({}));
      setProdLinkMsg(err.detail || "Errore nel salvataggio");
    }
  };

  const delProdLink = async (key) => {
    const res = await api("/api/product-links", {
      method: "POST",
      body: JSON.stringify({ key, url: "" }),
    });
    if (res.ok) await loadProdLinks();
  };

  const loadRaffle = async () => {
    const res = await api("/api/raffle");
    if (res.ok) setRaffle(await res.json());
  };

  const saveRaffle = async (patch) => {
    const next = { ...raffle, ...patch };
    const res = await api("/api/raffle", {
      method: "POST",
      body: JSON.stringify({
        active: next.active,
        title: next.title,
        price: next.price,
        note: next.note || "",
        pay_link: next.pay_link || "",
      }),
    });
    if (res.ok) setRaffle(next);
  };

  const uploadPrize = async (file) => {
    if (!file) return;
    setRaffleUploading(true);
    const fd = new FormData();
    fd.append("name", rafflePrizeName);
    fd.append("file", file);
    const res = await apiUpload("/api/raffle/prizes", fd);
    setRaffleUploading(false);
    if (res.ok) {
      setRafflePrizeName("");
      await loadRaffle();
    }
  };

  const delPrize = async (id) => {
    const res = await api(`/api/raffle/prizes/${id}`, { method: "DELETE" });
    if (res.ok) await loadRaffle();
  };

  const addPrizeQuick = async () => {
    if (!rafflePrizeName.trim()) return;
    const fd = new FormData();
    fd.append("name", rafflePrizeName.trim());
    const res = await apiUpload("/api/raffle/prizes", fd);
    if (res.ok) {
      setRafflePrizeName("");
      await loadRaffle();
    }
  };

  const loadRaffleEntries = async () => {
    const res = await api("/api/raffle/entries");
    if (res.ok) setRaffleEntries(await res.json());
  };

  const markEntryPaid = async (id, status) => {
    const res = await api(`/api/raffle/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    if (res.ok) await loadRaffleEntries();
  };

  const delEntry = async (id) => {
    const res = await api(`/api/raffle/entries/${id}`, { method: "DELETE" });
    if (res.ok) await loadRaffleEntries();
  };

  const resetRaffle = async () => {
    const res = await api("/api/raffle/reset", { method: "POST" });
    if (res.ok) await loadRaffleEntries();
  };

  const loadServices = async () => {    const res = await api("/api/services");
    if (res.ok) {
      const list = await res.json();
      setServices(list);
      setSvcEdits(
        Object.fromEntries(
          list.map((s) => [s.id, { nome: s.nome, prezzo: String(s.prezzo), durata: String(s.durata) }])
        )
      );
    }
  };

  const saveService = async (id) => {
    const e = svcEdits[id];
    if (!e) return;
    setSvcError("");
    const res = await api(`/api/services/${id}`, {
      method: "PUT",
      body: JSON.stringify({ nome: e.nome, prezzo: Number(e.prezzo), durata: Number(e.durata) }),
    });
    if (res.ok) await loadServices();
    else setSvcError("Controlla i dati del servizio (nome, prezzo, durata).");
  };

  const deleteService = async (id) => {
    setSvcError("");
    const res = await api(`/api/services/${id}`, { method: "DELETE" });
    if (res.ok) await loadServices();
  };

  const addService = async () => {
    if (!newSvc.nome.trim() || !newSvc.prezzo) {
      setSvcError("Inserisci nome e prezzo del nuovo servizio.");
      return;
    }
    setSvcError("");
    const res = await api("/api/services", {
      method: "POST",
      body: JSON.stringify({
        nome: newSvc.nome,
        prezzo: Number(newSvc.prezzo),
        durata: Number(newSvc.durata),
      }),
    });
    if (res.ok) {
      setNewSvc({ nome: "", prezzo: "", durata: "30" });
      await loadServices();
    } else {
      setSvcError("Controlla i dati del servizio (nome, prezzo, durata).");
    }
  };

  const addTeam = async () => {
    const nome = teamName.trim();
    if (!nome) return;
    const res = await api("/api/team", { method: "POST", body: JSON.stringify({ nome }) });
    if (res.ok) {
      setTeamName("");
      loadTeam();
    }
  };

  const removeTeam = async (id) => {
    const res = await api(`/api/team/${id}`, { method: "DELETE" });
    if (res.ok) loadTeam();
  };

  const [promo, setPromo] = useState(null);
  const [promoFile, setPromoFile] = useState(null);
  const [promoUploading, setPromoUploading] = useState(false);

  const loadPromo = async () => {
    const res = await api("/api/promo-video");
    if (res.ok) {
      const d = await res.json();
      setPromo(d && d.storage_path ? d : null);
    }
  };

  const uploadPromo = async () => {
    if (!promoFile || promoUploading) return;
    setPromoUploading(true);
    setUploadError("");
    const fd = new FormData();
    fd.append("file", promoFile);
    try {
      const res = await fetch(`${BACKEND}/api/promo-video`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (res.ok) {
        setPromoFile(null);
        await loadPromo();
      } else {
        setUploadError(await readError(res));
      }
    } catch {
      setUploadError("Errore di rete: controlla la connessione e riprova.");
    }
    setPromoUploading(false);
  };

  const deletePromo = async () => {
    const res = await api("/api/promo-video", { method: "DELETE" });
    if (res.ok) setPromo(null);
  };

  const [address, setAddress] = useState("");
  const [addressSaved, setAddressSaved] = useState(false);
  const [reviewsUrl, setReviewsUrl] = useState("");
  const [reviewsSaved, setReviewsSaved] = useState(false);
  const [tenantCode, setTenantCode] = useState("");
  const [tenants, setTenants] = useState([]);
  const [newTenant, setNewTenant] = useState({ salon_name: "", owner_name: "", telefono: "", price: "" });
  const [tenantBusy, setTenantBusy] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null); // {id, salon_name} → modal confirm
  const [payForms, setPayForms] = useState({}); // tenantId -> {date, amount, note, months, open}
  const [broadcasts, setBroadcasts] = useState([]);
  const [newBc, setNewBc] = useState({ title: "", body: "" });
  const [bcBusy, setBcBusy] = useState(false);
  const [waCfg, setWaCfg] = useState({ phone: "", apikey: "" });
  const [waSaved, setWaSaved] = useState(false);

  const loadBroadcasts = async () => {
    const res = await api("/api/broadcasts");
    if (res.ok) setBroadcasts(await res.json());
  };

  const publishBroadcast = async () => {
    if (bcBusy || !newBc.title.trim()) return;
    setBcBusy(true);
    const res = await api("/api/broadcasts", { method: "POST", body: JSON.stringify(newBc) });
    if (res.ok) {
      setNewBc({ title: "", body: "" });
      await loadBroadcasts();
    }
    setBcBusy(false);
  };

  const removeBroadcast = async (id) => {
    await api(`/api/broadcasts/${id}`, { method: "DELETE" });
    await loadBroadcasts();
  };

  const loadWaCfg = async () => {
    const res = await api("/api/settings/whatsapp-config");
    if (res.ok) setWaCfg(await res.json());
  };

  const saveWaCfg = async () => {
    const res = await api("/api/settings/whatsapp-config", { method: "POST", body: JSON.stringify(waCfg) });
    if (res.ok) {
      setWaSaved(true);
      setTimeout(() => setWaSaved(false), 2000);
    }
  };

  const loadTenants = async () => {
    const res = await api("/api/tenants");
    if (res.ok) setTenants(await res.json());
  };

  const createTenant = async () => {
    if (tenantBusy || !newTenant.salon_name.trim()) return;
    setTenantBusy(true);
    const res = await api("/api/tenants", {
      method: "POST",
      body: JSON.stringify({
        salon_name: newTenant.salon_name,
        owner_name: newTenant.owner_name,
        telefono: newTenant.telefono,
        price: Number(newTenant.price) || 0,
      }),
    });
    if (res.ok) {
      setNewTenant({ salon_name: "", owner_name: "", telefono: "", price: "" });
      await loadTenants();
    }
    setTenantBusy(false);
  };

  const addPayment = async (tid) => {
    const f = payForms[tid] || {};
    if (!f.date || !f.amount) return;
    const res = await api(`/api/tenants/${tid}/payments`, {
      method: "POST",
      body: JSON.stringify({ date: f.date, amount: Number(f.amount), note: f.note || "", months: Number(f.months) || 1 }),
    });
    if (res.ok) {
      setPayForms((p) => ({ ...p, [tid]: { open: true } }));
      await loadTenants();
    }
  };

  const toggleBlockTenant = async (tid, blocked) => {
    await api(`/api/tenants/${tid}/block`, { method: "POST", body: JSON.stringify({ blocked }) });
    await loadTenants();
  };

  const changePrice = async (tid, price) => {
    await api(`/api/tenants/${tid}`, { method: "PUT", body: JSON.stringify({ price: Number(price) || 0 }) });
    await loadTenants();
  };

  const deleteTenant = async () => {
    // Modal de confirmación propio (window.confirm no funciona en PWA/móvil)
    if (!tenantToDelete) return;
    const res = await api(`/api/tenants/${tenantToDelete.id}`, { method: "DELETE" });
    setTenantToDelete(null);
    if (res.ok) await loadTenants();
  };

  const copyText = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
  };

  const confirmPayment = async (bid) => {
    const res = await api(`/api/bookings/${bid}/pay-confirm`, { method: "POST" });
    if (res.ok) loadBookings();
  };

  // Métodos de pago del salón (cuentas del titolare)
  const [payCfg, setPayCfg] = useState({ sumup: "", paypal: "", satispay: "", iban: "" });
  const [openPay, setOpenPay] = useState(() => localStorage.getItem("admin_pay_open") === "1");
  const [paySaved, setPaySaved] = useState(false);

  const loadPayCfg = async () => {
    const res = await api("/api/settings/payment-methods");
    if (res.ok) setPayCfg(await res.json());
  };

  const savePayCfg = async () => {
    const res = await api("/api/settings/payment-methods", {
      method: "POST",
      body: JSON.stringify(payCfg),
    });
    if (res.ok) {
      setPaySaved(true);
      setTimeout(() => setPaySaved(false), 2000);
    }
  };

  // Contactos WhatsApp configurables (titolare, socio/jefe, cualquier número)
  const [waContacts, setWaContacts] = useState([]);
  const [openWaContacts, setOpenWaContacts] = useState(() => localStorage.getItem("admin_wa_open") === "1");
  const [waContactsSaved, setWaContactsSaved] = useState(false);

  const loadWaContacts = async () => {
    const res = await api("/api/settings/wa-contacts");
    if (res.ok) setWaContacts(await res.json());
  };

  const saveWaContacts = async () => {
    const res = await api("/api/settings/wa-contacts", {
      method: "POST",
      body: JSON.stringify({ contacts: waContacts }),
    });
    if (res.ok) {
      setWaContactsSaved(true);
      setTimeout(() => setWaContactsSaved(false), 2000);
      loadWaContacts();
    }
  };

  // Galería IA de estilos (inspiración en el modal "Prova il tuo taglio")
  const [openGallery, setOpenGallery] = useState(() => localStorage.getItem("admin_gallery_open") === "1");
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryStatus, setGalleryStatus] = useState("");

  const loadGallery = async () => {
    const res = await api("/api/ai/style-gallery");
    if (res.ok) {
      const d = await res.json();
      setGalleryItems(d.items || []);
      setGalleryStatus(d.status || "");
    }
  };

  const generateGallery = async () => {
    const res = await api("/api/ai/style-gallery/generate", { method: "POST" });
    if (!res.ok) return;
    setGalleryStatus("generating");
    for (let i = 0; i < 15; i++) {
      await new Promise((r) => setTimeout(r, 10000));
      const g = await api("/api/ai/style-gallery");
      if (g.ok) {
        const d = await g.json();
        setGalleryItems(d.items || []);
        setGalleryStatus(d.status || "");
        if ((d.status || "") !== "generating") break;
      }
    }
  };

  const tenantLogin = async (e) => {
    e.preventDefault();
    setError("");
    setTenantToken(tenantCode.trim());
    const res = await api("/api/auth/me");
    if (res.ok) {
      setUser(await res.json());
      loadBookings();
      loadCalStatus();
      loadBroadcasts();
      loadWeekSummary();
      loadInventory();
      if (!TENANT_MODE) { loadSocioContact(); loadHours(); loadPayCfg(); loadGallery(); loadWaContacts(); }
      if (!TENANT_MODE) loadTenants(); else loadWaCfg();
    } else {
      clearTenantToken();
      setUser(false);
      setError("Codice non valido");
    }
  };

  const loadReviewsUrl = async () => {
    const res = await api("/api/settings/reviews-url");
    if (res.ok) {
      const d = await res.json();
      setReviewsUrl(d.url || "");
    }
  };

  const saveReviewsUrl = async () => {
    const res = await api("/api/settings/reviews-url", {
      method: "POST",
      body: JSON.stringify({ url: reviewsUrl }),
    });
    if (res.ok) {
      setReviewsSaved(true);
      setTimeout(() => setReviewsSaved(false), 2000);
    }
  };

  const loadAddress = async () => {
    const res = await api("/api/settings/address");
    if (res.ok) {
      const d = await res.json();
      setAddress(d.address || "");
    }
  };

  const saveAddress = async () => {
    const res = await api("/api/settings/address", {
      method: "POST",
      body: JSON.stringify({ address }),
    });
    if (res.ok) {
      setAddressSaved(true);
      setTimeout(() => setAddressSaved(false), 2000);
    }
  };

  const setStatus = async (id, status) => {
    const res = await api(`/api/bookings/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setBookings((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
    }
  };

  const deleteBooking = async (id) => {
    const res = await api(`/api/bookings/${id}`, { method: "DELETE" });
    if (res.ok) setBookings((list) => list.filter((x) => x.id !== id));
  };

  const loadCalStatus = async () => {
    const res = await api("/api/calendar/status");
    if (res.ok) setCal(await res.json());
    loadMedia();
    loadTeam();
    loadStaffAccess();
    loadBlocked();
    loadClosedDays();
    loadBreaks();
    loadProdLinks();
    loadRaffle();
    loadRaffleEntries();
    loadFeatures();
    loadServices();
    loadPromo();
    loadAddress();
    loadReviewsUrl();
    loadTeamStatus();
    loadStats();
  };

  useEffect(() => {
    if (!user) return undefined;
    const iv = setInterval(loadTeamStatus, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const connectCalendar = async () => {
    const res = await api("/api/oauth/calendar/login");
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.authorization_url) {
      window.location.href = data.authorization_url;
    } else {
      setCalError(typeof data.detail === "string" ? data.detail : "Calendar non configurato");
    }
  };

  const normalizePhone = (tel) => {
    let d = String(tel || "").replace(/\D/g, "");
    if (d.startsWith("00")) d = d.slice(2);
    if (d && !d.startsWith("39") && d.length <= 10) d = "39" + d;
    return d;
  };

  const notifyClient = (b) => {
    const d = normalizePhone(b.telefono);
    const link = `${window.location.origin}/cita/${b.id}`;
    const payNote = b.payment?.status === "segnalato" ? " Abbiamo ricevuto la segnalazione del tuo pagamento, grazie!" : "";
    const text = `Ciao ${b.nome}! Il tuo appuntamento da Barber Latin è CONFERMATO: ${b.servizio}, ${b.giorno} alle ${b.ora}.${payNote} Qui trovi il tuo promemoria con lo stato della cita: ${link} — Ti aspettiamo!`;
    return { web: `https://wa.me/${d}?text=${encodeURIComponent(text)}`, intent: waIntent(d, text) };
  };

  const reminderClient = (b) => {
    const d = normalizePhone(b.telefono);
    const text = `Ciao ${b.nome}! Promemoria del tuo appuntamento da Barber Latin: ${b.servizio}, ${b.giorno} alle ${b.ora}. Ti aspettiamo!`;
    return { web: `https://wa.me/${d}?text=${encodeURIComponent(text)}`, intent: waIntent(d, text) };
  };

  const STATUS_CLS = {
    richiesta: "bg-emerald-500/15 text-emerald-400",
    confermata: "bg-amber-400/15 text-amber-400",
    annullata: "bg-red-500/15 text-red-400",
    attesa: "bg-zinc-500/20 text-zinc-300",
    completata: "bg-emerald-500/15 text-emerald-400",
    assente: "bg-orange-500/15 text-orange-400",
  };

  useEffect(() => {
    (async () => {
      const res = await api("/api/auth/me");
      if (res.ok) {
        setUser(await res.json());
        loadBookings();
        loadCalStatus();
        loadBroadcasts();
        loadWeekSummary();
        loadInventory();
        if (!TENANT_MODE) { loadSocioContact(); loadHours(); loadPayCfg(); loadGallery(); loadWaContacts(); }
        if (!TENANT_MODE) loadTenants(); else loadWaCfg();
      } else {
        setUser(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setError("");
    const res = await api("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (res.ok) {
      setUser(await res.json());
      loadBookings();
      loadCalStatus();
      loadBroadcasts();
      loadWeekSummary();
      loadInventory();
      if (!TENANT_MODE) { loadSocioContact(); loadHours(); loadPayCfg(); loadGallery(); loadWaContacts(); }
      if (!TENANT_MODE) loadTenants();
    } else {
      const data = await res.json().catch(() => ({}));
      setError(typeof data.detail === "string" ? data.detail : "Errore di accesso");
    }
  };

  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    if (TENANT_MODE) clearTenantToken();
    setUser(false);
    setBookings([]);
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-white" data-testid="admin-page">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col px-6 py-8">
        <header className="mb-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400/15 text-amber-400">
            <Scissors size={20} />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold">Le tue prenotazioni</h1>
            <p className="text-xs text-zinc-500">
              {TENANT_MODE
                ? `${(user && user.name) || tenantSlug()} — area riservata`
                : "Barber Latin — area riservata"}
            </p>
          </div>
        </header>

        {user === null && <p className="text-sm text-zinc-500">Caricamento…</p>}

        {user === false && TENANT_MODE && (
          <form onSubmit={tenantLogin} className="flex flex-col gap-4" data-testid="tenant-login-form">
            <p className="text-xs leading-relaxed text-zinc-500">
              Inserisci il codice segreto del tuo salone (te lo ha dato chi ti ha condiviso l&apos;app).
            </p>
            <input
              data-testid="tenant-code"
              type="password"
              required
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value)}
              placeholder="Codice segreto"
              className="h-12 rounded-2xl border border-white/10 bg-zinc-900 px-4 text-sm outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
            />
            {error && (
              <p data-testid="admin-error" className="text-sm font-semibold text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="tenant-login-submit"
              type="submit"
              className="h-12 rounded-2xl bg-amber-400 text-sm font-extrabold uppercase tracking-widest text-zinc-950 transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-[0.98]"
            >
              Accedi
            </button>
          </form>
        )}

        {user === false && !TENANT_MODE && (
          <form onSubmit={login} className="flex flex-col gap-4" data-testid="admin-login-form">
            <input
              data-testid="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="h-12 rounded-2xl border border-white/10 bg-zinc-900 px-4 text-sm outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
            />
            <input
              data-testid="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="h-12 rounded-2xl border border-white/10 bg-zinc-900 px-4 text-sm outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
            />
            {error && (
              <p data-testid="admin-error" className="text-sm font-semibold text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              className="h-12 rounded-2xl bg-amber-400 text-sm font-extrabold uppercase tracking-widest text-zinc-950 transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-[0.98]"
            >
              Accedi
            </button>
          </form>
        )}

        {user && user !== false && (
          <>
            {teamStatus.length > 0 && (
              <div
                data-testid="team-live-status"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <p className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Team in tempo reale
                </p>
                <div className="flex flex-col gap-2">
                  {teamStatus.map((s, i) => (
                    <div
                      key={s.barbiere}
                      data-testid={`team-status-${i}`}
                      className="flex items-center gap-3 rounded-xl border border-white/5 bg-zinc-950/60 px-3 py-2.5"
                    >
                      <span
                        data-testid={`team-dot-${i}`}
                        className={`h-3 w-3 shrink-0 rounded-full ${
                          s.stato === "in_orario" || s.stato === "in_ritardo"
                            ? "animate-pulse bg-emerald-400"
                            : s.stato === "completato"
                                ? "bg-amber-400"
                                : "bg-emerald-400"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-white">
                          {s.barbiere}
                          {s.agenda_piena && (
                            <span
                              data-testid={`team-full-${i}`}
                              className="ml-2 rounded-full bg-amber-400/20 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-amber-300"
                            >
                              Pieno
                            </span>
                          )}
                        </p>
                        <p className="truncate text-[11px] text-zinc-400">
                          {s.stato === "in_orario" &&
                            `Con ${s.cliente} (${s.servizio}) — finisce alle ${s.fine}`}
                          {s.stato === "in_ritardo" && `Con ${s.cliente} (${s.servizio})`}
                          {s.stato === "completato" && "Giornata completata 🎉"}
                          {s.stato === "libero" && "Disponibile"}
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] font-bold text-zinc-500">
                        {s.oggi_fatti}/{s.oggi_fatti + s.oggi_rimasti} oggi
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {cal && (
              <div
                data-testid="calendar-status"
                className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/15 text-amber-400">
                    <CalendarCheck size={18} />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-white">Google Calendar</p>
                    <p className="text-[11px] text-zinc-500">
                      {cal.connected
                        ? `Connesso — ${cal.email}`
                        : "Le conferme creano eventi automatici"}
                    </p>
                    {cal.revoked && (
                      <p data-testid="calendar-revoked-warning" className="mt-1 text-[11px] font-semibold text-red-400">
                        Calendario disconnesso (Google ha revocato l&apos;accesso): tocca
                        &quot;Connetti&quot; qui sotto e le prenotazioni tornano subito sul telefono.
                      </p>
                    )}
                    {cal.whatsapp === "quota" && (
                      <p data-testid="whatsapp-quota-warning" className="mt-1 text-[11px] font-semibold text-red-400">
                        Avvisi WhatsApp fermi: CallMeBot ha esaurito i messaggi gratuiti.
                        Riattivalo su callmebot.com e torna a funzionare da solo.
                      </p>
                    )}
                  </div>
                </div>
                {!cal.connected && (
                  <button
                    data-testid="btn-connect-calendar"
                    onClick={connectCalendar}
                    className="shrink-0 rounded-xl bg-amber-400 px-4 py-2 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300"
                  >
                    Connetti
                  </button>
                )}
              </div>
            )}
            {calError && (
              <p data-testid="calendar-error" className="mb-4 text-xs font-semibold text-red-400">
                {calError}
              </p>
            )}

            <div
              data-testid="admin-services"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Servizi e prezzi
              </p>
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                Modifica nome, prezzo e durata quando vuoi: Jordan userà subito i nuovi dati.
              </p>
              <div className="flex flex-col gap-2">
                {services.map((s) => {
                  const e = svcEdits[s.id] || { nome: s.nome, prezzo: String(s.prezzo), durata: String(s.durata) };
                  return (
                    <div key={s.id} data-testid={`service-row-${s.id}`} className="flex items-center gap-2">
                      <input
                        data-testid={`service-nome-${s.id}`}
                        value={e.nome}
                        onChange={(ev) => setSvcEdits({ ...svcEdits, [s.id]: { ...e, nome: ev.target.value } })}
                        className="h-9 min-w-0 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none focus:border-amber-400/50"
                      />
                      <input
                        data-testid={`service-prezzo-${s.id}`}
                        value={e.prezzo}
                        onChange={(ev) => setSvcEdits({ ...svcEdits, [s.id]: { ...e, prezzo: ev.target.value } })}
                        inputMode="decimal"
                        title="Prezzo €"
                        className="h-9 w-16 shrink-0 rounded-lg border border-white/10 bg-zinc-950 px-2 text-center text-xs text-white outline-none focus:border-amber-400/50"
                      />
                      <input
                        data-testid={`service-durata-${s.id}`}
                        value={e.durata}
                        onChange={(ev) => setSvcEdits({ ...svcEdits, [s.id]: { ...e, durata: ev.target.value } })}
                        inputMode="numeric"
                        title="Durata minuti"
                        className="h-9 w-16 shrink-0 rounded-lg border border-white/10 bg-zinc-950 px-2 text-center text-xs text-white outline-none focus:border-amber-400/50"
                      />
                      <button
                        data-testid={`service-save-${s.id}`}
                        onClick={() => saveService(s.id)}
                        className="h-9 shrink-0 rounded-lg bg-amber-400 px-3 text-[10px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
                      >
                        Salva
                      </button>
                      <button
                        data-testid={`service-delete-${s.id}`}
                        onClick={() => deleteService(s.id)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
                <div className="mt-1 flex items-center gap-2 border-t border-white/10 pt-3">
                  <input
                    data-testid="service-new-nome"
                    value={newSvc.nome}
                    onChange={(e) => setNewSvc({ ...newSvc, nome: e.target.value })}
                    placeholder="Nuovo servizio"
                    className="h-9 min-w-0 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                  />
                  <input
                    data-testid="service-new-prezzo"
                    value={newSvc.prezzo}
                    onChange={(e) => setNewSvc({ ...newSvc, prezzo: e.target.value })}
                    inputMode="decimal"
                    placeholder="€"
                    className="h-9 w-16 shrink-0 rounded-lg border border-white/10 bg-zinc-950 px-2 text-center text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                  />
                  <input
                    data-testid="service-new-durata"
                    value={newSvc.durata}
                    onChange={(e) => setNewSvc({ ...newSvc, durata: e.target.value })}
                    inputMode="numeric"
                    placeholder="min"
                    className="h-9 w-16 shrink-0 rounded-lg border border-white/10 bg-zinc-950 px-2 text-center text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                  />
                  <button
                    data-testid="service-add"
                    onClick={addService}
                    className="h-9 shrink-0 rounded-lg bg-emerald-500 px-3 text-[10px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
              {svcError && (
                <p data-testid="service-error" className="mt-2 text-xs font-semibold text-red-400">
                  {svcError}
                </p>
              )}
              <p className="mt-2 text-[11px] text-zinc-600">
                Formato: nome · prezzo in € · durata in minuti (multipli di 30 consigliati).
              </p>
            </div>

            <div
              data-testid="admin-team"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Team del salone
              </p>
              <p className="mb-3 text-[11px] text-zinc-600">
                Jordan chiederà ai clienti con chi vogliono l&apos;appuntamento.
              </p>
              <div className="mb-3 flex gap-2">
                <input
                  data-testid="team-input"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Nome del dipendente…"
                  className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                />
                <button
                  data-testid="team-add"
                  onClick={addTeam}
                  disabled={!teamName.trim()}
                  className="shrink-0 rounded-xl bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-emerald-400 disabled:opacity-40"
                >
                  Aggiungi
                </button>
              </div>
              <div className="flex flex-col gap-2" data-testid="team-list">
                {team.map((t, i) => (
                  <div key={t.id}>
                    <div data-testid={`team-member-${i}`} className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400/15 font-display text-sm font-bold text-amber-400">
                        {t.nome[0].toUpperCase()}
                      </span>
                      <span className="flex-1 text-sm font-bold text-white">
                        {t.nome}
                        {(t.custom_hours || (t.days_off || []).length > 0) && (
                          <span className="ml-2 rounded-full bg-amber-400/15 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-amber-300">
                            Orari suoi
                          </span>
                        )}
                      </span>
                      <button
                        data-testid={`team-hours-toggle-${i}`}
                        onClick={() => toggleBarberHours(t)}
                        title="Orari e giorni di riposo"
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 ${
                          barberHoursEdit[t.id]
                            ? "border-amber-400/50 text-amber-400"
                            : "border-white/10 text-zinc-500 hover:border-amber-400/40 hover:text-amber-400"
                        }`}
                      >
                        <Clock size={12} />
                      </button>
                      <button
                        data-testid={`team-delete-${i}`}
                        onClick={() => removeTeam(t.id)}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors duration-200 hover:border-red-500/40 hover:text-red-400"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    {barberHoursEdit[t.id] && (
                      <div
                        data-testid={`team-hours-editor-${i}`}
                        className="mt-2 flex flex-col gap-3 rounded-xl border border-white/10 bg-zinc-950/70 p-3"
                      >
                        <label className="flex items-center gap-2 text-xs font-semibold text-zinc-300">
                          <input
                            type="checkbox"
                            data-testid={`team-hours-custom-${i}`}
                            checked={barberHoursEdit[t.id].custom}
                            onChange={(e) =>
                              setBarberHoursEdit((p) => ({
                                ...p,
                                [t.id]: { ...p[t.id], custom: e.target.checked },
                              }))
                            }
                            className="h-4 w-4 accent-amber-400"
                          />
                          Orari personalizzati (altrimenti usa quelli del salone)
                        </label>
                        {barberHoursEdit[t.id].custom && (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <span className="w-14 text-[10px] font-bold uppercase text-zinc-500">
                                Mattina
                              </span>
                              <input
                                type="time"
                                data-testid={`team-hours-mo-${i}`}
                                value={barberHoursEdit[t.id].mo}
                                onChange={(e) =>
                                  setBarberHoursEdit((p) => ({ ...p, [t.id]: { ...p[t.id], mo: e.target.value } }))
                                }
                                className="h-9 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none focus:border-amber-400/50"
                              />
                              <span className="text-zinc-600">–</span>
                              <input
                                type="time"
                                data-testid={`team-hours-mc-${i}`}
                                value={barberHoursEdit[t.id].mc}
                                onChange={(e) =>
                                  setBarberHoursEdit((p) => ({ ...p, [t.id]: { ...p[t.id], mc: e.target.value } }))
                                }
                                className="h-9 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none focus:border-amber-400/50"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-14 text-[10px] font-bold uppercase text-zinc-500">
                                Sera
                              </span>
                              <input
                                type="time"
                                data-testid={`team-hours-eo-${i}`}
                                value={barberHoursEdit[t.id].eo}
                                onChange={(e) =>
                                  setBarberHoursEdit((p) => ({ ...p, [t.id]: { ...p[t.id], eo: e.target.value } }))
                                }
                                className="h-9 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none focus:border-amber-400/50"
                              />
                              <span className="text-zinc-600">–</span>
                              <input
                                type="time"
                                data-testid={`team-hours-ec-${i}`}
                                value={barberHoursEdit[t.id].ec}
                                onChange={(e) =>
                                  setBarberHoursEdit((p) => ({ ...p, [t.id]: { ...p[t.id], ec: e.target.value } }))
                                }
                                className="h-9 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none focus:border-amber-400/50"
                              />
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                            Giorni di riposo settimanali
                          </p>
                          <div className="flex gap-1.5">
                            {DAY_CHIPS.map((lbl, wd) => (
                              <button
                                key={wd}
                                data-testid={`team-dayoff-${i}-${wd}`}
                                onClick={() =>
                                  setBarberHoursEdit((p) => {
                                    const cur = p[t.id].days_off;
                                    return {
                                      ...p,
                                      [t.id]: {
                                        ...p[t.id],
                                        days_off: cur.includes(wd)
                                          ? cur.filter((x) => x !== wd)
                                          : [...cur, wd].sort(),
                                      },
                                    };
                                  })
                                }
                                className={`flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-extrabold transition-colors duration-150 ${
                                  (barberHoursEdit[t.id].days_off || []).includes(wd)
                                    ? "bg-red-500/80 text-white"
                                    : "bg-white/5 text-zinc-400 hover:bg-white/10"
                                }`}
                              >
                                {lbl}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          data-testid={`team-hours-save-${i}`}
                          onClick={() => saveBarberHours(t.id)}
                          className="h-9 rounded-xl bg-amber-400 text-[10px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
                        >
                          Salva orari di {t.nome}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div
              data-testid="admin-staff-access"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Accesso dipendenti
              </p>
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                Condividi il link con un dipendente: dal suo telefono potrà registrare
                prenotazioni. Puoi attivare o disattivare l&apos;accesso quando vuoi.
              </p>
              <div className="mb-3 flex gap-2">
                <input
                  data-testid="access-input"
                  value={newAccessName}
                  onChange={(e) => setNewAccessName(e.target.value)}
                  placeholder="Nome del dipendente…"
                  className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                />
                <button
                  data-testid="access-add"
                  onClick={createAccess}
                  disabled={!newAccessName.trim()}
                  className="shrink-0 rounded-xl bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-emerald-400 disabled:opacity-40"
                >
                  Crea link
                </button>
              </div>
              <div className="flex flex-col gap-2" data-testid="access-list">
                {staffAccess.map((a, i) => (
                  <div
                    key={a.id}
                    data-testid={`access-row-${i}`}
                    className="flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-zinc-950/60 p-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">
                      {a.nome}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest ${
                        a.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/40 text-zinc-500"
                      }`}
                    >
                      {a.active ? "Attivo" : "Disattivo"}
                    </span>
                    {a.device_bound && (
                      <button
                        data-testid={`access-reset-${i}`}
                        onClick={() => resetAccessDevice(a)}
                        title="Link già aperto su un telefono: tocca per svincolarlo"
                        className="h-8 shrink-0 rounded-full border border-blue-500/30 px-3 text-[10px] font-extrabold uppercase tracking-widest text-blue-300 transition-colors hover:bg-blue-500/10"
                      >
                        Reset tel
                      </button>
                    )}
                    <button
                      data-testid={`access-open-${i}`}
                      onClick={() =>
                        window.open(`${window.location.origin}/staff/${a.token}`, "_blank")
                      }
                      className="h-8 shrink-0 rounded-full border border-blue-500/30 px-3 text-[10px] font-extrabold uppercase tracking-widest text-blue-300 transition-colors hover:bg-blue-500/10"
                    >
                      Apri
                    </button>
                    <button
                      data-testid={`access-copy-${i}`}
                      onClick={() => copyAccessLink(a)}
                      disabled={!a.active}
                      className="h-8 shrink-0 rounded-full bg-amber-400/15 px-3 text-[10px] font-extrabold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-400/25 disabled:opacity-40"
                    >
                      {copiedAccess === a.id ? "Copiato ✓" : "Copia link"}
                    </button>
                    <button
                      data-testid={`access-toggle-${i}`}
                      onClick={() => toggleAccess(a)}
                      className={`h-8 shrink-0 rounded-full px-3 text-[10px] font-extrabold uppercase tracking-widest transition-colors ${
                        a.active
                          ? "border border-white/10 text-zinc-300 hover:border-amber-400/40 hover:text-amber-300"
                          : "bg-emerald-500 text-white hover:bg-emerald-400"
                      }`}
                    >
                      {a.active ? "Disattiva" : "Attiva"}
                    </button>
                    <button
                      data-testid={`access-delete-${i}`}
                      onClick={() => deleteAccess(a)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {staffAccess.length === 0 && (
                  <p className="text-xs text-zinc-600">Nessun accesso creato.</p>
                )}
              </div>
            </div>

            <div
              data-testid="admin-blocked"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="blocked-toggle"
                onClick={() => {
                  const v = !blockedOpen;
                  setBlockedOpen(v);
                  localStorage.setItem("admin_blocked_open", v ? "1" : "0");
                }}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Numeri bloccati
                  {blockedNumbers.length > 0 && (
                    <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">
                      {blockedNumbers.length}
                    </span>
                  )}
                </span>
                <span className="text-xs text-zinc-500">{blockedOpen ? "▾" : "▸"}</span>
              </button>
              {blockedOpen && (
                <div className="mt-2">
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                I numeri in questa lista non possono prenotare: né con Jordan né dal link dei
                dipendenti. Utile contro prenotazioni false.
              </p>
              <div className="mb-3 flex gap-2">
                <input
                  data-testid="blocked-input"
                  value={newBlocked}
                  onChange={(e) => setNewBlocked(e.target.value)}
                  placeholder="Numero da bloccare (es. 3331234567)"
                  className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-red-500/50"
                />
                <button
                  data-testid="blocked-add"
                  onClick={addBlocked}
                  disabled={!newBlocked.trim()}
                  className="shrink-0 rounded-xl bg-red-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-red-400 disabled:opacity-40"
                >
                  Blocca
                </button>
              </div>
              <div className="flex flex-col gap-2" data-testid="blocked-list">
                {blockedNumbers.map((n, i) => (
                  <div
                    key={n.id}
                    data-testid={`blocked-row-${i}`}
                    className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/5 p-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">
                      {n.telefono}
                    </span>
                    <button
                      data-testid={`blocked-delete-${i}`}
                      onClick={() => delBlocked(n.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-emerald-500/40 hover:text-emerald-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {blockedNumbers.length === 0 && (
                  <p className="text-xs text-zinc-600">Nessun numero bloccato.</p>
                )}
                </div>
                </div>
              )}
            </div>

            <div
              data-testid="admin-closed-days"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Giorni di chiusura
              </p>
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                Nei giorni chiusi Jordan non accetta prenotazioni e le propone un altro giorno. Le
                prenotazioni di Jordan in quei giorni finiscono in lista d&apos;attesa.
              </p>
              <div className="mb-3 flex flex-col gap-2">
                <div className="flex gap-2">
                  <input
                    data-testid="closed-date-input"
                    type="date"
                    value={closedDate}
                    onChange={(e) => setClosedDate(e.target.value)}
                    className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none [color-scheme:dark] focus:border-red-500/50"
                  />
                  <button
                    data-testid="closed-add"
                    onClick={addClosedDay}
                    disabled={!closedDate}
                    className="shrink-0 rounded-xl bg-red-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-red-400 disabled:opacity-40"
                  >
                    Chiudi
                  </button>
                </div>
                <input
                  data-testid="closed-note-input"
                  value={closedNote}
                  onChange={(e) => setClosedNote(e.target.value)}
                  placeholder="Nota (opzionale, es. ferie)"
                  className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-red-500/50"
                />
              </div>
              <div className="flex flex-col gap-2" data-testid="closed-list">
                {closedDays.map((c, i) => (
                  <div
                    key={c.id}
                    data-testid={`closed-row-${i}`}
                    className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/5 p-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">
                      {c.label}
                      {c.note && (
                        <span className="ml-2 text-[11px] font-normal text-zinc-500">
                          {c.note}
                        </span>
                      )}
                    </span>
                    <button
                      data-testid={`closed-delete-${i}`}
                      onClick={() => delClosedDay(c.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-emerald-500/40 hover:text-emerald-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {closedDays.length === 0 && (
                  <p className="text-xs text-zinc-600">Nessun giorno di chiusura.</p>
                )}
              </div>
            </div>

            <div
              data-testid="admin-breaks"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Pause del salone
              </p>
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                Le pause bloccano Jordan e compaiono come eventi nel tuo Google Calendar del
                telefono. Scegli &quot;Tutto il salone&quot; o un solo barbiere.
              </p>
              <div className="mb-3 flex flex-col gap-2">
                <input
                  data-testid="break-date-input"
                  type="date"
                  value={brk.date}
                  onChange={(e) => setBrk({ ...brk, date: e.target.value })}
                  className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none [color-scheme:dark] focus:border-amber-400/50"
                />
                <div className="flex gap-2">
                  <input
                    data-testid="break-start-input"
                    type="time"
                    value={brk.inizio}
                    onChange={(e) => setBrk({ ...brk, inizio: e.target.value })}
                    className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none [color-scheme:dark] focus:border-amber-400/50"
                  />
                  <input
                    data-testid="break-end-input"
                    type="time"
                    value={brk.fine}
                    onChange={(e) => setBrk({ ...brk, fine: e.target.value })}
                    className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none [color-scheme:dark] focus:border-amber-400/50"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    data-testid="break-barber-select"
                    value={brk.barbiere}
                    onChange={(e) => setBrk({ ...brk, barbiere: e.target.value })}
                    className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none focus:border-amber-400/50"
                  >
                    <option value="">Tutto il salone</option>
                    {team.map((t) => (
                      <option key={t.id} value={t.nome}>
                        {t.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    data-testid="break-add"
                    onClick={addBreak}
                    disabled={!brk.date || !brk.inizio || !brk.fine}
                    className="shrink-0 rounded-xl bg-amber-400 px-4 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300 disabled:opacity-40"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2" data-testid="breaks-list">
                {breaks.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`break-row-${i}`}
                    className="flex items-center gap-2 rounded-xl border border-amber-400/20 bg-amber-400/5 p-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">
                      {p.label} · {p.ora_inizio}-{p.ora_fine}
                      <span className="ml-2 text-[11px] font-normal text-zinc-500">
                        {p.barbiere || "Tutto il salone"}
                      </span>
                    </span>
                    <button
                      data-testid={`break-delete-${i}`}
                      onClick={() => delBreak(p.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {breaks.length === 0 && (
                  <p className="text-xs text-zinc-600">Nessuna pausa impostata.</p>
                )}
              </div>
            </div>

            <div
              data-testid="admin-product-links"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="prodlink-toggle"
                onClick={() => {
                  const v = !affOpen;
                  setAffOpen(v);
                  localStorage.setItem("admin_aff_open", v ? "1" : "0");
                }}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Link affiliato prodotti
                  {Object.keys(prodLinks).length > 0 && (
                    <span className="ml-2 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] text-amber-300">
                      {Object.keys(prodLinks).length} attivi
                    </span>
                  )}
                </span>
                <span className="text-xs text-zinc-500">{affOpen ? "▾" : "▸"}</span>
              </button>
              {affOpen && (
                <>
              <p className="mb-3 mt-2 text-[11px] leading-relaxed text-zinc-600">
                Crea la tua lista di vendita (tutti i prodotti che vuoi): scrivi il nome libero,
                incolla il link Amazon o l&apos;ASIN di 10 caratteri — il tuo tag affiliato si
                aggiunge da solo. I prodotti compaiono nella nube 🛍️ dei clienti. Senza link, la
                nube sparisce da sola.
              </p>
              <div className="mb-3 flex flex-col gap-2">
                <input
                  data-testid="prodlink-product"
                  list="prodlink-suggestions"
                  value={prodLinkSel}
                  onChange={(e) => {
                    setProdLinkSel(e.target.value);
                    setProdLinkMsg("");
                  }}
                  placeholder="Nome del prodotto (es. Cera mate forte)"
                  className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                />
                <datalist id="prodlink-suggestions">
                  {PRODUCTS.map((p) => (
                    <option key={p.name} value={p.name} />
                  ))}
                </datalist>
                <div className="flex gap-2">
                  <input
                    data-testid="prodlink-url"
                    value={prodLinkUrl}
                    onChange={(e) => {
                      setProdLinkUrl(e.target.value);
                      setProdLinkMsg("");
                    }}
                    placeholder="Link Amazon oppure ASIN (es. B08XYZ1234)"
                    className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                  />
                  <button
                    data-testid="prodlink-save"
                    onClick={saveProdLink}
                    disabled={!prodLinkSel.trim() || !prodLinkUrl.trim()}
                    className="shrink-0 rounded-xl bg-amber-400 px-4 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300 disabled:opacity-40"
                  >
                    Salva
                  </button>
                </div>
                {prodLinkMsg && (
                  <p
                    data-testid="prodlink-msg"
                    className={`text-[11px] font-semibold ${
                      prodLinkMsg === "ok" ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {prodLinkMsg === "ok" ? "Salvato — è già nella nube dei clienti 🛍️" : prodLinkMsg}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2" data-testid="prodlink-list">
                {Object.entries(prodLinks).map(([k, url], i) => (
                  <div
                    key={k}
                    data-testid={`prodlink-row-${i}`}
                    className="flex items-center gap-2 rounded-xl border border-amber-400/20 bg-amber-400/5 p-2"
                  >
                    <span className="min-w-0 flex-1 text-sm font-bold text-white">
                      {k}
                      <span className="block truncate text-[11px] font-normal text-zinc-500">
                        {url}
                      </span>
                    </span>
                    <button
                      data-testid={`prodlink-delete-${i}`}
                      onClick={() => delProdLink(k)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {Object.keys(prodLinks).length === 0 && (
                  <p className="text-xs text-zinc-600">
                    Nessun link personalizzato: i prodotti aprono la ricerca Amazon.
                  </p>
                )}
              </div>
                </>
              )}
            </div>

            {!TENANT_MODE && (
            <div
              data-testid="admin-raffle"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <div className="mb-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Tombola &amp; Sorteggio 🎟️
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-600">
                    Attivala: i clienti vedono la nuvola verde 🎟️ con i premi e comprano il numero
                    con il tuo link di pagamento SumUp.
                  </p>
                </div>
                <button
                  data-testid="raffle-toggle-btn"
                  onClick={() => saveRaffle({ active: !raffle.active })}
                  className={`relative h-8 w-14 shrink-0 rounded-full transition-colors duration-200 ${
                    raffle.active ? "bg-emerald-500" : "bg-zinc-700"
                  }`}
                >
                  <span
                    className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-all duration-200 ${
                      raffle.active ? "left-7" : "left-1"
                    }`}
                  />
                </button>
                <span
                  className={`shrink-0 text-[10px] font-extrabold uppercase tracking-widest ${
                    raffle.active ? "text-emerald-400" : "text-zinc-500"
                  }`}
                >
                  {raffle.active ? "Attiva" : "Off"}
                </span>
              </div>
              <div className="mb-3 flex flex-col gap-2">
                <input
                  data-testid="raffle-title"
                  value={raffle.title}
                  onChange={(e) => setRaffle({ ...raffle, title: e.target.value })}
                  placeholder="Titolo (es. Tombola di settembre)"
                  className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                />
                <div className="flex gap-2">
                  <input
                    data-testid="raffle-price"
                    value={raffle.price}
                    onChange={(e) => setRaffle({ ...raffle, price: e.target.value })}
                    placeholder="Prezzo del numero (es. 5€)"
                    className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                  />
                  <button
                    data-testid="raffle-save"
                    onClick={() => saveRaffle({})}
                    className="shrink-0 rounded-xl bg-emerald-400 px-4 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-emerald-300"
                  >
                    Salva
                  </button>
                </div>
                <textarea
                  data-testid="raffle-note"
                  value={raffle.note || ""}
                  onChange={(e) => setRaffle({ ...raffle, note: e.target.value })}
                  placeholder="Nota atractiva del sorteggio (es. 🎄 Solo 100 numeri, un solo vincitore: la fortuna può essere la tua!)"
                  rows={2}
                  className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-2 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                />
                <input
                  data-testid="raffle-paylink"
                  value={raffle.pay_link || ""}
                  onChange={(e) => setRaffle({ ...raffle, pay_link: e.target.value })}
                  placeholder="Link di pagamento alternativo (vuoto = SumUp attuale)"
                  className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                />
                <p className="text-[10px] text-zinc-600">
                  Metodo di pagamento: {raffle.pay_link ? "personalizzato attivo" : "SumUp (quello delle prenotazioni)"} — incolla un altro link
                  quando vuoi cambiarlo.
                </p>
              </div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                Premi 🏆 <span className="font-normal normal-case text-zinc-600">(1 o 2, foto opzionale)</span>
              </p>
              <div className="mb-3 flex flex-col gap-2">
                <input
                  data-testid="raffle-prize-name"
                  value={rafflePrizeName}
                  onChange={(e) => setRafflePrizeName(e.target.value)}
                  placeholder="Nome del premio (es. Taglio + Barba gratis)"
                  className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="raffle-prize-quick"
                    onClick={addPrizeQuick}
                    disabled={!rafflePrizeName.trim()}
                    className="h-11 flex-1 rounded-xl border border-emerald-400/40 text-[11px] font-bold uppercase tracking-widest text-emerald-300 transition-colors hover:bg-emerald-400/10 disabled:opacity-40"
                  >
                    ⚡ Aggiungi veloce
                  </button>
                  <label
                    data-testid="raffle-prize-upload"
                    className="flex h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-emerald-400/40 text-[11px] font-bold uppercase tracking-widest text-emerald-300 transition-colors hover:border-emerald-400"
                  >
                    {raffleUploading ? "Caricamento…" : "📷 Con foto"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        uploadPrize(e.target.files && e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
              <div className="flex flex-col gap-2" data-testid="raffle-prize-list">
                {(raffle.prizes || []).map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`raffle-prize-row-${i}`}
                    className="flex items-center gap-2 rounded-xl border border-emerald-400/20 bg-emerald-400/5 p-2"
                  >
                    {p.storage_path ? (
                      <img
                        src={`${BACKEND}/api/media/file/${p.storage_path}`}
                        alt={p.name || "Premio"}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-400/10 text-lg">
                        🏆
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">
                      {p.name || `Premio ${i + 1}`}
                    </span>
                    <button
                      data-testid={`raffle-prize-delete-${i}`}
                      onClick={() => delPrize(p.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {(raffle.prizes || []).length === 0 && (
                  <p className="text-xs text-zinc-600">Nessun premio ancora — aggiungi la foto.</p>
                )}
              </div>

              <div className="mt-4 mb-2 flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                  Partecipanti ({raffleEntries.length})
                </p>
                {raffleEntries.length > 0 && (
                  <button
                    data-testid="raffle-reset"
                    onClick={resetRaffle}
                    className="text-[10px] font-bold uppercase tracking-wider text-red-400/80 transition-colors hover:text-red-300"
                  >
                    Azzera lista
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2" data-testid="raffle-entries-list">
                {raffleEntries.map((e, i) => (
                  <div
                    key={e.id}
                    data-testid={`raffle-entry-row-${i}`}
                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-zinc-950/60 p-2"
                  >
                    <span className="w-8 shrink-0 text-center text-sm font-extrabold text-emerald-300">
                      {e.number}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-bold text-white">{e.nome}</span>
                      <span className="block text-[10px] text-zinc-500">{e.telefono}</span>
                    </span>
                    <button
                      data-testid={`raffle-entry-paid-${i}`}
                      onClick={() => markEntryPaid(e.id, e.status === "pagato" ? "riservato" : "pagato")}
                      className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider transition-colors ${
                        e.status === "pagato"
                          ? "border-emerald-400/40 bg-emerald-400/20 text-emerald-300"
                          : "border-amber-400/40 bg-amber-400/10 text-amber-300"
                      }`}
                    >
                      {e.status === "pagato" ? "Pagato ✓" : "Da confermare"}
                    </button>
                    <button
                      data-testid={`raffle-entry-del-${i}`}
                      onClick={() => delEntry(e.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {raffleEntries.length === 0 && (
                  <p className="text-xs text-zinc-600">Nessun numero riservato ancora.</p>
                )}
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-zinc-600">
                Il cliente compra il numero dal tablero; tu lo segni &quot;Pagato&quot; quando
                arriva il pagamento SumUp. Solo tu vedi questa lista — i clienti vedono solo i
                numeri occupati.
              </p>
            </div>
            )}

            <div
              data-testid="admin-week-summary"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="week-summary-toggle"
                onClick={() => togglePanel("admin_week_open", !openWeek, setOpenWeek)}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Resumen de la semana 📊
                </span>
                <span className="text-xs text-zinc-500">{openWeek ? "▾" : "▸"}</span>
              </button>
              {openWeek && weekSummary && (
                <div className="mt-3">
                  <div className="mb-3 flex gap-2">
                    <div className="grid flex-1 grid-cols-2 gap-1 rounded-xl bg-zinc-950 p-1">
                    {[["settimana", "Settimana"], ["mese", "Mese"]].map(([k, label]) => (
                      <button
                        key={k}
                        data-testid={`week-tab-${k}`}
                        onClick={() => setWeekTab(k)}
                        className={`rounded-lg py-1.5 text-[11px] font-extrabold uppercase tracking-widest transition-colors ${
                          weekTab === k ? "bg-amber-400 text-zinc-950" : "text-zinc-500 hover:text-white"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                    </div>
                    <button
                      data-testid="week-share-whatsapp"
                      onClick={() => {
                        const isWeek = weekTab === "settimana";
                        const citas = isWeek ? weekSummary.citas : weekSummary.mes_citas;
                        const ingresos = isWeek ? weekSummary.ingresos_stimati : weekSummary.mes_ingresos;
                        const perBarber = isWeek ? weekSummary.per_barbiere : weekSummary.mes_per_barbiere;
                        const periodo = isWeek
                          ? `Settimana ${weekSummary.settimana}`
                          : `Mese di ${weekSummary.mese_nome}`;
                        const lines = Object.entries(perBarber).map(
                          ([n, d]) => `• ${n}: ${d.citas} app. · ${d.ingresos}€`
                        );
                        const text = [
                          `📊 Resumen ${isWeek ? "settimanale" : "mensile"} — ${periodo}`,
                          `Appuntamenti: ${citas} · Incasso stimato: ${ingresos}€`,
                          ...lines,
                        ].join("\n");
                        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                      }}
                      className="shrink-0 rounded-xl bg-emerald-500/15 px-3 text-emerald-400 transition-colors hover:bg-emerald-500/25"
                      title="Condividi su WhatsApp"
                    >
                      <FaWhatsapp size={18} />
                    </button>
                  </div>
                  {(() => {
                    const isWeek = weekTab === "settimana";
                    const citas = isWeek ? weekSummary.citas : weekSummary.mes_citas;
                    const ingresos = isWeek ? weekSummary.ingresos_stimati : weekSummary.mes_ingresos;
                    const perBarber = isWeek ? weekSummary.per_barbiere : weekSummary.mes_per_barbiere;
                    const diff = isWeek ? citas - weekSummary.prev_citas : null;
                    const diffEur = isWeek ? ingresos - weekSummary.prev_ingresos : null;
                    return (
                      <>
                        <div className="mb-3 grid grid-cols-2 gap-2">
                          <div className="rounded-xl bg-zinc-950 p-3 text-center">
                            <p data-testid="week-citas" className="text-2xl font-extrabold text-white">
                              {citas}
                            </p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                              Appuntamenti
                            </p>
                            {isWeek && (
                              <p
                                data-testid="week-citas-diff"
                                className={`mt-1 text-[10px] font-bold ${
                                  diff > 0 ? "text-emerald-400" : diff < 0 ? "text-red-400" : "text-zinc-500"
                                }`}
                              >
                                {diff > 0 ? "▲" : diff < 0 ? "▼" : "="} {Math.abs(diff)} vs sett. scorsa
                              </p>
                            )}
                          </div>
                          <div className="rounded-xl bg-zinc-950 p-3 text-center">
                            <p data-testid="week-ingresos" className="text-2xl font-extrabold text-emerald-400">
                              {ingresos}€
                            </p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                              Incasso stimato
                            </p>
                            {isWeek && (
                              <p
                                data-testid="week-ingresos-diff"
                                className={`mt-1 text-[10px] font-bold ${
                                  diffEur > 0 ? "text-emerald-400" : diffEur < 0 ? "text-red-400" : "text-zinc-500"
                                }`}
                              >
                                {diffEur > 0 ? "▲" : diffEur < 0 ? "▼" : "="} {Math.abs(diffEur)}€ vs sett. scorsa
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="mb-2 text-[10px] uppercase tracking-widest text-zinc-600">
                          {isWeek ? `Settimana ${weekSummary.settimana}` : `Mese di ${weekSummary.mese_nome}`}
                        </p>
                        <div className="flex flex-col gap-1.5" data-testid="week-per-barber">
                          {Object.entries(perBarber).map(([name, d]) => (
                            <div
                              key={name}
                              className="flex items-center gap-2 rounded-lg bg-zinc-950 px-3 py-2"
                            >
                              <Scissors size={12} className="shrink-0 text-amber-400" />
                              <span className="min-w-0 flex-1 truncate text-xs font-bold text-white">
                                {name}
                              </span>
                              <span className="text-[11px] text-zinc-400">
                                {d.citas} {d.citas === 1 ? "appuntamento" : "appuntamenti"} · {d.ingresos}€
                              </span>
                            </div>
                          ))}
                          {Object.keys(perBarber).length === 0 && (
                            <p className="text-center text-[11px] text-zinc-600">
                              Nessun appuntamento in questo periodo.
                            </p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div
              data-testid="admin-inventory"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="inventory-toggle"
                onClick={() => togglePanel("admin_inv_open", !openInv, setOpenInv)}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Inventario — cose da comprare 🛒
                </span>
                <span className="text-xs text-zinc-500">{openInv ? "▾" : "▸"}</span>
              </button>
              {openInv && (
                <div className="mt-3">
                  <div className="mb-3 flex gap-2">
                    <input
                      data-testid="inventory-new-name"
                      value={newItem.nome}
                      onChange={(e) => setNewItem({ ...newItem, nome: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && addItem()}
                      placeholder="Es. Cera opaca, lame rasoio…"
                      className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                    />
                    <input
                      data-testid="inventory-new-note"
                      value={newItem.note}
                      onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && addItem()}
                      placeholder="Nota"
                      className="h-10 w-24 shrink-0 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                    />
                    <button
                      data-testid="inventory-add"
                      onClick={addItem}
                      disabled={!newItem.nome.trim()}
                      className="shrink-0 rounded-xl bg-amber-400 px-4 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300 disabled:opacity-40"
                    >
                      + Add
                    </button>
                  </div>
                  <div className="flex flex-col gap-1.5" data-testid="inventory-list">
                    {inventory.map((it) => (
                      <div
                        key={it.id}
                        data-testid={`inventory-item-${it.id}`}
                        className="flex items-center gap-2 rounded-lg bg-zinc-950 px-3 py-2"
                      >
                        <button
                          data-testid={`inventory-check-${it.id}`}
                          onClick={() => toggleItem(it.id)}
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
                            it.comprato
                              ? "border-emerald-400 bg-emerald-500 text-white"
                              : "border-white/20 text-transparent hover:border-emerald-400"
                          }`}
                        >
                          ✓
                        </button>
                        <span
                          className={`min-w-0 flex-1 truncate text-xs font-semibold ${
                            it.comprato ? "text-zinc-600 line-through" : "text-white"
                          }`}
                        >
                          {it.nome}
                          {it.note && <span className="text-zinc-500"> · {it.note}</span>}
                        </span>
                        <button
                          data-testid={`inventory-del-${it.id}`}
                          onClick={() => removeItem(it.id)}
                          className="shrink-0 text-zinc-600 transition-colors hover:text-red-400"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                    {inventory.length === 0 && (
                      <p className="text-center text-[11px] text-zinc-600">
                        Nessun articolo. Aggiungi quello che manca in salone.
                      </p>
                    )}
                  </div>
                  {inventory.some((it) => !it.comprato) && (
                    <button
                      data-testid="inventory-share"
                      onClick={shareInventory}
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                    >
                      <FaWhatsapp size={15} /> Condividi al socio su WhatsApp
                    </button>
                  )}
                </div>
              )}
            </div>

            <div
              data-testid="admin-features"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="features-toggle"
                onClick={() => {
                  const v = !featOpen;
                  setFeatOpen(v);
                  localStorage.setItem("admin_feat_open", v ? "1" : "0");
                }}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Funzioni dell&apos;app ⚙️
                </span>
                <span className="text-xs text-zinc-500">{featOpen ? "▾" : "▸"}</span>
              </button>
              {featOpen && (
                <div className="mt-2">
                  <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                    Accendi o spegni ogni funzione dell&apos;app dei clienti. Per aggiungere una
                    funzione nuova, chiedila nella chat di Emergent.
                  </p>
                  <div className="flex flex-col gap-2">
                    {FEATURE_ITEMS.map((f) => (
                      <div
                        key={f.key}
                        data-testid={`feature-row-${f.key}`}
                        className="flex items-center gap-3 rounded-xl border border-white/5 bg-zinc-950/50 p-2.5"
                      >
                        <span className="min-w-0 flex-1 text-xs font-bold text-white">
                          {f.label}
                        </span>
                        <button
                          data-testid={`feature-toggle-${f.key}`}
                          onClick={() => toggleFeature(f.key)}
                          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${
                            features[f.key] ? "bg-emerald-500" : "bg-zinc-700"
                          }`}
                        >
                          <span
                            className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all duration-200 ${
                              features[f.key] ? "left-6" : "left-1"
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              data-testid="admin-address"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Indirizzo del salone
              </p>
              <p className="mb-3 text-[11px] text-zinc-600">
                Quando lo aggiungi, i clienti lo vedono in Info con bottone Google Maps.
              </p>
              <div className="flex gap-2">
                <input
                  data-testid="address-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Es. Via Roma 10, 20100 Milano"
                  className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                />
                <button
                  data-testid="address-save"
                  onClick={saveAddress}
                  className="shrink-0 rounded-xl bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-emerald-400"
                >
                  {addressSaved ? "Salvato ✓" : "Salva"}
                </button>
              </div>
            </div>

            <div
              data-testid="admin-reviews-url"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="reviews-toggle"
                onClick={() => togglePanel("admin_rev_open", !openRev, setOpenRev)}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Link recensioni Google
                </span>
                <span className="text-xs text-zinc-500">{openRev ? "▾" : "▸"}</span>
              </button>
              {openRev && (
              <div className="mt-2">
              <p className="mb-3 text-[11px] text-zinc-600">
                Quando hai il link della tua scheda Google Maps, incollalo qui: il bottone blu
                &quot;Lascia una Recensione&quot; aprirà direttamente le tue recensioni. Vuoto =
                manda al tuo WhatsApp.
              </p>
              <div className="flex gap-2">
                <input
                  data-testid="reviews-url-input"
                  value={reviewsUrl}
                  onChange={(e) => setReviewsUrl(e.target.value)}
                  placeholder="Es. https://g.page/r/…/review"
                  className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                />
                <button
                  data-testid="reviews-url-save"
                  onClick={saveReviewsUrl}
                  className="shrink-0 rounded-xl bg-blue-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-blue-400"
                >
                  {reviewsSaved ? "Salvato ✓" : "Salva"}
                </button>
              </div>
              </div>
              )}
            </div>

            {!TENANT_MODE && (
              <div
                data-testid="admin-tenants"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="tenants-toggle"
                  onClick={() => togglePanel("admin_ten_open", !openTen, setOpenTen)}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Barbieri — condividi la tua app
                  </span>
                  <span className="text-xs text-zinc-500">{openTen ? "▾" : "▸"}</span>
                </button>
                {openTen && (
                <div className="mt-2">
                <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                  Crea l&apos;app per altri barbieri con la TUA struttura (prenotazioni, Jordan, il tuo
                  link Amazon). La Tombola resta solo tua. Segna i pagamenti mensili: se non pagano,
                  l&apos;app si blocca da sola (o la blocchi tu).
                </p>
                <div className="mb-3 flex flex-col gap-2">
                  <input
                    data-testid="tenant-new-name"
                    value={newTenant.salon_name}
                    onChange={(e) => setNewTenant({ ...newTenant, salon_name: e.target.value })}
                    placeholder="Nome del salone (es. Barber Mario)"
                    className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                  />
                  <div className="flex gap-2">
                    <input
                      data-testid="tenant-new-owner"
                      value={newTenant.owner_name}
                      onChange={(e) => setNewTenant({ ...newTenant, owner_name: e.target.value })}
                      placeholder="Proprietario"
                      className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                    />
                    <input
                      data-testid="tenant-new-price"
                      type="number"
                      min="0"
                      value={newTenant.price}
                      onChange={(e) => setNewTenant({ ...newTenant, price: e.target.value })}
                      placeholder="€/mese"
                      className="h-10 w-24 shrink-0 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                    />
                  </div>
                  <button
                    data-testid="tenant-create"
                    onClick={createTenant}
                    disabled={tenantBusy || !newTenant.salon_name.trim()}
                    className="h-10 rounded-xl bg-amber-400 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300 disabled:opacity-40"
                  >
                    {tenantBusy ? "Creazione…" : "Crea app per questo barbiere"}
                  </button>
                </div>
                <div className="flex flex-col gap-3" data-testid="tenants-list">
                  {tenants.map((t) => (
                    <div
                      key={t.id}
                      data-testid={`tenant-card-${t.slug}`}
                      className="rounded-xl border border-white/10 bg-zinc-950 p-3"
                    >
                      <div className="flex items-center gap-2">
                        <p className="min-w-0 flex-1 truncate text-sm font-bold text-white">
                          {t.salon_name}
                        </p>
                        <span
                          data-testid={`tenant-status-${t.slug}`}
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest ${
                            t.status === "attivo"
                              ? "bg-emerald-500/15 text-emerald-400"
                              : t.status === "in_scadenza"
                                ? "bg-amber-400/15 text-amber-400"
                                : "bg-red-500/15 text-red-400"
                          }`}
                        >
                          {t.status === "attivo"
                            ? `Attiva (${t.days_left}g)`
                            : t.status === "in_scadenza"
                              ? `Scade tra ${t.days_left}g`
                              : "Bloccata"}
                        </span>
                      </div>
                      <p className="mt-0.5 text-[11px] text-zinc-500">
                        {t.owner_name} · Quota: {t.price}€/mese · Attiva fino al{" "}
                        {t.active_until ? t.active_until.slice(0, 10) : "—"}
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          data-testid={`tenant-copy-app-${t.slug}`}
                          onClick={() => copyText(`${window.location.origin}/s/${t.code}`)}
                          className="flex-1 rounded-lg bg-white/5 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:bg-white/10"
                        >
                          Copia link app
                        </button>
                        <button
                          data-testid={`tenant-copy-admin-${t.slug}`}
                          onClick={() =>
                            copyText(`${window.location.origin}/t/${t.slug}/admin — codice: ${t.admin_token}`)
                          }
                          className="flex-1 rounded-lg bg-white/5 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:bg-white/10"
                        >
                          Copia link admin
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          data-testid={`tenant-pay-toggle-${t.slug}`}
                          onClick={() =>
                            setPayForms((p) => ({
                              ...p,
                              [t.id]: { ...(p[t.id] || {}), open: !(p[t.id] || {}).open },
                            }))
                          }
                          className="rounded-lg bg-emerald-500/15 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-emerald-400 transition-colors hover:bg-emerald-500/25"
                        >
                          + Pagamento
                        </button>
                        <input
                          data-testid={`tenant-price-${t.slug}`}
                          type="number"
                          min="0"
                          defaultValue={t.price}
                          onBlur={(e) => {
                            if (Number(e.target.value) !== t.price) changePrice(t.id, e.target.value);
                          }}
                          className="h-8 w-20 rounded-lg border border-white/10 bg-zinc-900 px-2 text-xs text-white outline-none"
                        />
                        <span className="text-[10px] text-zinc-600">€/mese</span>
                        <button
                          data-testid={`tenant-block-${t.slug}`}
                          onClick={() => toggleBlockTenant(t.id, !t.blocked_manual)}
                          className={`ml-auto rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                            t.blocked_manual
                              ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                              : "bg-red-500/15 text-red-400 hover:bg-red-500/25"
                          }`}
                        >
                          {t.blocked_manual ? "Sblocca" : "Blocca"}
                        </button>
                        <button
                          data-testid={`tenant-delete-${t.slug}`}
                          onClick={() => setTenantToDelete({ id: t.id, salon_name: t.salon_name })}
                          className="rounded-lg bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 transition-colors hover:bg-red-500/15 hover:text-red-400"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {(payForms[t.id] || {}).open && (
                        <div className="mt-2 flex flex-col gap-2 rounded-lg bg-zinc-900 p-2.5">
                          <div className="flex gap-2">
                            <input
                              data-testid={`tenant-pay-date-${t.slug}`}
                              type="date"
                              value={(payForms[t.id] || {}).date || ""}
                              onChange={(e) =>
                                setPayForms((p) => ({ ...p, [t.id]: { ...(p[t.id] || {}), date: e.target.value } }))
                              }
                              className="h-9 min-w-0 flex-1 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none"
                            />
                            <input
                              data-testid={`tenant-pay-amount-${t.slug}`}
                              type="number"
                              min="0"
                              placeholder="€"
                              value={(payForms[t.id] || {}).amount || ""}
                              onChange={(e) =>
                                setPayForms((p) => ({ ...p, [t.id]: { ...(p[t.id] || {}), amount: e.target.value } }))
                              }
                              className="h-9 w-20 shrink-0 rounded-lg border border-white/10 bg-zinc-950 px-2 text-xs text-white outline-none"
                            />
                            <button
                              data-testid={`tenant-pay-save-${t.slug}`}
                              onClick={() => addPayment(t.id)}
                              className="shrink-0 rounded-lg bg-emerald-500 px-3 text-[10px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                            >
                              Salva
                            </button>
                          </div>
                          {(t.payments || []).length > 0 && (
                            <div className="flex flex-col gap-1" data-testid={`tenant-payments-${t.slug}`}>
                              {t.payments.map((pay) => (
                                <p key={pay.id} className="text-[11px] text-zinc-400">
                                  📅 {pay.date} — {pay.amount}€
                                  {pay.months > 1 ? ` (${pay.months} mesi)` : ""}
                                  {pay.note ? ` · ${pay.note}` : ""}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {tenants.length === 0 && (
                    <p className="text-center text-[11px] text-zinc-600">
                      Nessun barbiere ancora. Creane uno qui sopra e condividi il link.
                    </p>
                  )}
                </div>
                </div>
                )}
              </div>
            )}

            <div
              data-testid="admin-broadcasts"
              className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
            >
              <button
                data-testid="broadcasts-toggle"
                onClick={() => togglePanel("admin_bc_open", !openBc, setOpenBc)}
                className="flex w-full items-center gap-2"
              >
                <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                  {TENANT_MODE ? "Aggiornamenti dal provider" : "Aggiornamenti per i barbieri"}
                </span>
                <span className="text-xs text-zinc-500">{openBc ? "▾" : "▸"}</span>
              </button>
              {openBc && (
              <div className="mt-2">
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                {TENANT_MODE
                  ? "Qui vedi le novità e i miglioramenti che il provider condivide con te."
                  : "Le novità arrivano ai barbieri SOLO se le pubblichi qui (es. cambi di prezzo, nuove funzioni)."}
              </p>
              {!TENANT_MODE && (
                <div className="mb-3 flex flex-col gap-2">
                  <input
                    data-testid="broadcast-title"
                    value={newBc.title}
                    onChange={(e) => setNewBc({ ...newBc, title: e.target.value })}
                    placeholder="Titolo (es. Nuovo prezzo da ottobre)"
                    className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                  />
                  <textarea
                    data-testid="broadcast-body"
                    value={newBc.body}
                    onChange={(e) => setNewBc({ ...newBc, body: e.target.value })}
                    placeholder="Dettagli (opzionale)"
                    rows={2}
                    className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-2 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                  />
                  <button
                    data-testid="broadcast-publish"
                    onClick={publishBroadcast}
                    disabled={bcBusy || !newBc.title.trim()}
                    className="h-10 rounded-xl bg-amber-400 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300 disabled:opacity-40"
                  >
                    {bcBusy ? "Invio…" : "Condividi aggiornamento"}
                  </button>
                </div>
              )}
              <div className="flex flex-col gap-2" data-testid="broadcasts-list">
                {broadcasts.map((b) => (
                  <div key={b.id} className="rounded-xl border border-white/10 bg-zinc-950 p-3">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-white">{b.title}</p>
                        {b.body && <p className="mt-0.5 text-[11px] text-zinc-400">{b.body}</p>}
                        <p className="mt-1 text-[10px] text-zinc-600">
                          {(b.created_at || "").slice(0, 10)}
                        </p>
                      </div>
                      {!TENANT_MODE && (
                        <button
                          data-testid={`broadcast-delete-${b.id}`}
                          onClick={() => removeBroadcast(b.id)}
                          className="shrink-0 text-zinc-600 transition-colors hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {broadcasts.length === 0 && (
                  <p className="text-center text-[11px] text-zinc-600">Nessun aggiornamento.</p>
                )}
              </div>
              </div>
              )}
            </div>

            {!TENANT_MODE && (
              <div
                data-testid="admin-agenda-share"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="agenda-toggle"
                  onClick={() => {
                    const v = !openAg;
                    togglePanel("admin_ag_open", v, setOpenAg);
                    if (v && !agendaShare.token) loadAgendaShare();
                  }}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Condividi agenda (socio)
                  </span>
                  <span className="text-xs text-zinc-500">{openAg ? "▾" : "▸"}</span>
                </button>
                {openAg && (
                  <div className="mt-2">
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                      Il link si aggiorna da solo in tempo reale (prossimo mese): il tuo socio
                      vede le prenotazioni divise per barbiere e gli orari liberi. Niente telefoni né
                      dati sensibili. Lo mandi una volta sola.
                    </p>
                    <div className="flex gap-2">
                      <input
                        data-testid="agenda-socio-phone"
                        value={socioPhone}
                        onChange={(e) => setSocioPhone(e.target.value)}
                        inputMode="tel"
                        placeholder="WhatsApp del socio (es. 39333…)"
                        className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                      />
                      <button
                        data-testid="agenda-send-whatsapp"
                        onClick={sendAgendaWhatsApp}
                        disabled={!socioPhone.trim()}
                        className="shrink-0 rounded-xl bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400 disabled:opacity-40"
                      >
                        Invia WhatsApp
                      </button>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        data-testid="agenda-copy"
                        onClick={() => copyText(agendaUrl)}
                        className="flex-1 rounded-xl bg-white/5 py-2.5 text-[11px] font-extrabold uppercase tracking-widest text-zinc-300 transition-colors hover:bg-white/10"
                      >
                        Copia link (solo lettura)
                      </button>
                      <button
                        data-testid="agenda-rotate"
                        onClick={rotateAgendaShare}
                        className="rounded-xl bg-white/5 px-4 py-2.5 text-[11px] font-extrabold uppercase tracking-widest text-zinc-400 transition-colors hover:bg-white/10"
                      >
                        Rigenera
                      </button>
                    </div>
                    <button
                      data-testid="agenda-copy-manage"
                      onClick={() => copyText(agendaManageUrl)}
                      className="mt-2 w-full rounded-xl border border-red-500/30 bg-red-500/10 py-2.5 text-[11px] font-extrabold uppercase tracking-widest text-red-300 transition-colors hover:bg-red-500/20"
                    >
                      Copia link con eliminazione (solo per te)
                    </button>
                    {agendaShare.token && (
                      <p data-testid="agenda-url" className="mt-2 break-all text-[10px] text-zinc-600">
                        {agendaUrl}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {!TENANT_MODE && (
              <div
                data-testid="admin-socio-contact"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="socio-toggle"
                  onClick={() => togglePanel("admin_socio_open", !openSocio, setOpenSocio)}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Contatto del socio
                  </span>
                  <span className="text-xs text-zinc-500">{openSocio ? "▾" : "▸"}</span>
                </button>
                {openSocio && (
                  <div className="mt-2">
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                      Il contatto appare nella pagina Info dell&apos;app, sotto le altre card.
                      Lascia vuoto per nasconderlo.
                    </p>
                    <div className="flex flex-col gap-2">
                      <input
                        data-testid="socio-name"
                        value={socioContact.name}
                        onChange={(e) => setSocioContact({ ...socioContact, name: e.target.value })}
                        placeholder="Nome del socio (es. Marco)"
                        className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                      />
                      <div className="flex gap-2">
                        <input
                          data-testid="socio-phone"
                          value={socioContact.phone}
                          onChange={(e) => setSocioContact({ ...socioContact, phone: e.target.value })}
                          inputMode="tel"
                          placeholder="WhatsApp (es. 393331234567)"
                          className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                        />
                        <button
                          data-testid="socio-save"
                          onClick={saveSocioContact}
                          className="shrink-0 rounded-xl bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                        >
                          {socioSaved ? "Salvato ✓" : "Salva"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!TENANT_MODE && (
              <div
                data-testid="admin-payment-methods"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="pay-methods-toggle"
                  onClick={() => togglePanel("admin_pay_open", !openPay, setOpenPay)}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Metodi di pagamento 💳
                  </span>
                  <span className="text-xs text-zinc-500">{openPay ? "▾" : "▸"}</span>
                </button>
                {openPay && (
                  <div className="mt-2">
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                      I TUOI conti di incasso: il cliente li vede dopo aver prenotato e può pagare
                      in anticipo. Quando paga, tu ricevi l&apos;avviso su WhatsApp e confermi con un
                      tocco (badge &quot;Da verificare&quot; → &quot;Pagato ✓&quot;). Vuoto = metodo nascosto.
                    </p>
                    <div className="flex flex-col gap-2">
                      <input
                        data-testid="pay-sumup-input"
                        value={payCfg.sumup}
                        onChange={(e) => setPayCfg({ ...payCfg, sumup: e.target.value })}
                        placeholder="Link SumUp (carta) — es. https://pay.sumup.com/..."
                        className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                      />
                      <input
                        data-testid="pay-paypal-input"
                        value={payCfg.paypal}
                        onChange={(e) => setPayCfg({ ...payCfg, paypal: e.target.value })}
                        placeholder="Link PayPal.Me (opzionale)"
                        className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                      />
                      <input
                        data-testid="pay-satispay-input"
                        value={payCfg.satispay}
                        onChange={(e) => setPayCfg({ ...payCfg, satispay: e.target.value })}
                        placeholder="Link Satispay (opzionale)"
                        className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                      />
                      <input
                        data-testid="pay-iban-input"
                        value={payCfg.iban}
                        onChange={(e) => setPayCfg({ ...payCfg, iban: e.target.value })}
                        placeholder="IBAN per bonifico (opzionale)"
                        className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                      />
                      <button
                        data-testid="pay-methods-save"
                        onClick={savePayCfg}
                        className="h-10 rounded-xl bg-amber-400 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
                      >
                        {paySaved ? "Salvato ✓" : "Salva metodi"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!TENANT_MODE && (
              <div
                data-testid="admin-wa-contacts"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="wa-contacts-toggle"
                  onClick={() => togglePanel("admin_wa_open", !openWaContacts, setOpenWaContacts)}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Contatti WhatsApp 📱
                  </span>
                  <span className="text-xs text-zinc-500">{openWaContacts ? "▾" : "▸"}</span>
                </button>
                {openWaContacts && (
                  <div className="mt-2">
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                      I numeri che i clienti vedono nella pagina Info per scrivervi su WhatsApp:
                      tu, il socio, chi vuoi. Aggiungi, modifica o elimina liberamente.
                    </p>
                    <div className="flex flex-col gap-2">
                      {waContacts.map((c, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <input
                            data-testid={`wa-contact-name-${ci}`}
                            value={c.name}
                            onChange={(e) =>
                              setWaContacts(waContacts.map((x, xi) => (xi === ci ? { ...x, name: e.target.value } : x)))
                            }
                            placeholder="Nome (es. Titolare)"
                            className="h-10 w-2/5 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-[#25D366]/50"
                          />
                          <input
                            data-testid={`wa-contact-phone-${ci}`}
                            value={c.phone}
                            onChange={(e) =>
                              setWaContacts(waContacts.map((x, xi) => (xi === ci ? { ...x, phone: e.target.value } : x)))
                            }
                            inputMode="tel"
                            placeholder="393881234567"
                            className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-[#25D366]/50"
                          />
                          <button
                            data-testid={`wa-contact-del-${ci}`}
                            onClick={() => setWaContacts(waContacts.filter((_, xi) => xi !== ci))}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                      {waContacts.length < 5 && (
                        <button
                          data-testid="wa-contact-add"
                          onClick={() => setWaContacts([...waContacts, { name: "", phone: "" }])}
                          className="h-9 rounded-xl border border-dashed border-white/15 text-[10px] font-extrabold uppercase tracking-widest text-zinc-400 transition-colors hover:border-[#25D366]/40 hover:text-[#25D366]"
                        >
                          + Aggiungi numero
                        </button>
                      )}
                      <button
                        data-testid="wa-contacts-save"
                        onClick={saveWaContacts}
                        className="h-10 rounded-xl bg-[#25D366] text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:brightness-110"
                      >
                        {waContactsSaved ? "Salvato ✓" : "Salva contatti"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!TENANT_MODE && (
              <div
                data-testid="admin-ai-gallery"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="ai-gallery-toggle"
                  onClick={() => togglePanel("admin_gallery_open", !openGallery, setOpenGallery)}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Galleria tagli IA ✨
                  </span>
                  <span className="text-xs text-zinc-500">{openGallery ? "▾" : "▸"}</span>
                </button>
                {openGallery && (
                  <div className="mt-2">
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                      Esempi di tagli generati con l&apos;IA: i clienti li vedono come ispirazione
                      nel pulsante &quot;Prova il tuo taglio&quot;. Tocca per generarli (circa 1 minuto).
                    </p>
                    <button
                      data-testid="ai-gallery-generate"
                      onClick={generateGallery}
                      disabled={galleryStatus === "generating"}
                      className="mb-3 h-10 w-full rounded-xl bg-amber-400 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300 disabled:opacity-50"
                    >
                      {galleryStatus === "generating"
                        ? "Generando… (~1 min)"
                        : galleryItems.length > 0
                          ? "Rigenera esempi IA"
                          : "Genera esempi IA"}
                    </button>
                    {galleryItems.length > 0 && (
                      <div data-testid="ai-gallery-thumbs" className="flex gap-2 overflow-x-auto pb-1">
                        {galleryItems.map((g) => (
                          <div key={g.style} className="w-20 shrink-0 overflow-hidden rounded-lg border border-white/10">
                            <img
                              src={`${BACKEND}${g.url}`}
                              alt={g.style}
                              className="h-16 w-20 object-cover"
                              loading="lazy"
                            />
                            <p className="truncate px-1 py-0.5 text-center text-[8px] font-bold text-zinc-400">
                              {g.style}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!TENANT_MODE && (
              <div
                data-testid="admin-hours"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <button
                  data-testid="hours-toggle"
                  onClick={() => togglePanel("admin_hours_open", !openHours, setOpenHours)}
                  className="flex w-full items-center gap-2"
                >
                  <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Orari del salone
                  </span>
                  <span className="text-xs text-zinc-500">{openHours ? "▾" : "▸"}</span>
                </button>
                {openHours && (
                  <div className="mt-2">
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                      Cambia gli orari di apertura. Si applicano subito a prenotazioni, Jordan e
                      disponibilità.
                    </p>
                    <div className="flex flex-col gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Mattina</p>
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="hours-morning-open"
                          type="time"
                          value={hours.morning_open}
                          onChange={(e) => setHours({ ...hours, morning_open: e.target.value })}
                          className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none focus:border-amber-400/50"
                        />
                        <span className="text-xs text-zinc-500">→</span>
                        <input
                          data-testid="hours-morning-close"
                          type="time"
                          value={hours.morning_close}
                          onChange={(e) => setHours({ ...hours, morning_close: e.target.value })}
                          className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none focus:border-amber-400/50"
                        />
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Sera</p>
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="hours-evening-open"
                          type="time"
                          value={hours.evening_open}
                          onChange={(e) => setHours({ ...hours, evening_open: e.target.value })}
                          className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none focus:border-amber-400/50"
                        />
                        <span className="text-xs text-zinc-500">→</span>
                        <input
                          data-testid="hours-evening-close"
                          type="time"
                          value={hours.evening_close}
                          onChange={(e) => setHours({ ...hours, evening_close: e.target.value })}
                          className="h-10 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none focus:border-amber-400/50"
                        />
                      </div>
                      <button
                        data-testid="hours-save"
                        onClick={saveHours}
                        className="h-10 rounded-xl bg-amber-400 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
                      >
                        {hoursSaved ? "Salvato ✓" : "Salva orari"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {TENANT_MODE && (
              <div
                data-testid="admin-whatsapp-config"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Notifiche WhatsApp del tuo salone
                </p>
                <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                  Per ricevere le prenotazioni sul TUO WhatsApp: manda &quot;I allow callmebot to send
                  me messages&quot; al +34 644 51 95 23 su WhatsApp, copia l&apos;apikey che ti arriva e
                  incolla qui sotto con il tuo numero (formato internazionale, es. 393331234567).
                </p>
                <div className="flex flex-col gap-2">
                  <input
                    data-testid="wa-config-phone"
                    value={waCfg.phone}
                    onChange={(e) => setWaCfg({ ...waCfg, phone: e.target.value })}
                    placeholder="Il tuo numero (es. 393331234567)"
                    className="h-10 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                  />
                  <div className="flex gap-2">
                    <input
                      data-testid="wa-config-apikey"
                      value={waCfg.apikey}
                      onChange={(e) => setWaCfg({ ...waCfg, apikey: e.target.value })}
                      placeholder="Apikey CallMeBot"
                      className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                    />
                    <button
                      data-testid="wa-config-save"
                      onClick={saveWaCfg}
                      className="shrink-0 rounded-xl bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                    >
                      {waSaved ? "Salvato ✓" : "Salva"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {clientStats && (
              <div
                data-testid="client-stats"
                className="mb-5 grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <div className="rounded-xl bg-zinc-950/60 p-3 text-center">
                  <p data-testid="stat-clients" className="font-display text-3xl font-bold text-amber-400">
                    {clientStats.clienti}
                  </p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Clienti del salone
                  </p>
                </div>
                <div className="rounded-xl bg-zinc-950/60 p-3 text-center">
                  <p data-testid="stat-installs" className="font-display text-3xl font-bold text-emerald-400">
                    {clientStats.app_installate}
                  </p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    App installate
                  </p>
                </div>
              </div>
            )}

            {weeklyStats.length > 0 && (
              <div
                data-testid="growth-chart"
                className="mb-5 rounded-2xl border border-white/10 bg-zinc-900 p-4"
              >
                <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Crescita del salone
                </p>
                <p className="mb-3 text-[11px] text-zinc-600">Prenotazioni per settimana</p>
                <div className="flex h-28 items-end gap-2">
                  {weeklyStats.map((w, i) => {
                    const max = Math.max(...weeklyStats.map((x) => x.count), 1);
                    const h = Math.max(6, Math.round((w.count / max) * 100));
                    const isLast = i === weeklyStats.length - 1;
                    return (
                      <div key={w.label} className="flex flex-1 flex-col items-center gap-1">
                        <span className="text-[9px] font-bold text-zinc-400">{w.count}</span>
                        <div
                          data-testid={`growth-bar-${i}`}
                          className={`w-full rounded-t-md transition-all ${
                            isLast ? "bg-amber-400" : "bg-zinc-700"
                          }`}
                          style={{ height: `${h}%` }}
                        />
                        <span className="text-[9px] text-zinc-600">{w.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mb-5 flex items-center justify-between">
              <p className="text-xs text-zinc-500">
                {bookings.length} richieste · {user.email}
              </p>
              <div className="flex gap-2">
                <button
                  data-testid="admin-add-manual"
                  onClick={() => {
                    setShowManual((s) => !s);
                    setManualError("");
                  }}
                  className="flex h-9 items-center gap-1.5 rounded-full bg-emerald-500 px-4 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                >
                  + Aggiungi
                </button>
                <button
                  data-testid="admin-refresh"
                  onClick={loadBookings}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:text-amber-400"
                >
                  <RefreshCw size={15} />
                </button>
                <button
                  data-testid="admin-logout"
                  onClick={logout}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:text-red-400"
                >
                  <LogOut size={15} />
                </button>
              </div>
            </div>

            {loadingList && <p className="text-sm text-zinc-500">Caricamento…</p>}

            {bookings.filter((b) => b.status === "attesa").length > 0 && (
              <button
                data-testid="waitlist-cloud"
                onClick={() =>
                  document
                    .querySelector('[data-testid="admin-bookings"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="fixed bottom-6 right-5 z-[60] flex items-center gap-2 rounded-full border border-amber-400/50 bg-zinc-900/95 px-4 py-2.5 shadow-[0_4px_24px_rgba(251,191,36,0.35)] backdrop-blur transition-transform duration-200 active:scale-95"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
                </span>
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-amber-300">
                  Coda: {bookings.filter((b) => b.status === "attesa").length}{" "}
                  {bookings.filter((b) => b.status === "attesa").length === 1
                    ? "cliente"
                    : "clienti"}
                </span>
              </button>
            )}

            {showManual && (
              <div
                data-testid="manual-booking-form"
                className="mb-5 flex flex-col gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">
                  Nuova prenotazione manuale
                </p>
                <input
                  data-testid="manual-nome"
                  value={manual.nome}
                  onChange={(e) => setManual({ ...manual, nome: e.target.value })}
                  placeholder="Nome e cognome del cliente"
                  className="h-11 rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                />
                <input
                  data-testid="manual-telefono"
                  value={manual.telefono}
                  onChange={(e) => setManual({ ...manual, telefono: e.target.value })}
                  placeholder="Telefono (opzionale)"
                  className="h-11 rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                />
                <div className="flex gap-2">
                  <select
                    data-testid="manual-servizio"
                    value={manual.servizio}
                    onChange={(e) => setManual({ ...manual, servizio: e.target.value })}
                    className="h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-emerald-400/50"
                  >
                    <option value="">Servizio…</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.nome}>
                        {s.nome} — {s.prezzo}€
                      </option>
                    ))}
                  </select>
                  <select
                    data-testid="manual-barbiere"
                    value={manual.barbiere}
                    onChange={(e) => setManual({ ...manual, barbiere: e.target.value })}
                    className="h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-emerald-400/50"
                  >
                    <option value="">Barbiere…</option>
                    {team.map((t) => (
                      <option key={t.id} value={t.nome}>
                        {t.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="manual-data"
                    type="date"
                    value={manual.data}
                    onChange={(e) => setManual({ ...manual, data: e.target.value })}
                    className="h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-emerald-400/50"
                  />
                  <select
                    data-testid="manual-ora"
                    value={manual.ora}
                    onChange={(e) => setManual({ ...manual, ora: e.target.value })}
                    className="h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-emerald-400/50"
                  >
                    <option value="">Ora…</option>
                    {TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                {manualError && (
                  <p data-testid="manual-error" className="text-xs font-semibold text-red-400">
                    {manualError}
                  </p>
                )}
                <button
                  data-testid="manual-save"
                  onClick={saveManual}
                  disabled={manualSaving}
                  className="h-11 rounded-xl bg-emerald-500 text-xs font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400 disabled:opacity-40"
                >
                  {manualSaving ? "Salvataggio…" : "Salva prenotazione"}
                </button>
                <p className="text-[11px] leading-relaxed text-zinc-600">
                  La prenotazione viene aggiunta anche a Google Calendar ed entra nella pulizia
                  settimanale automatica.
                </p>
              </div>
            )}

            {!loadingList && bookings.length === 0 && (
              <p data-testid="admin-empty" className="text-sm text-zinc-500">
                Nessuna richiesta di prenotazione per ora.
              </p>
            )}

            <div className="flex flex-col gap-3" data-testid="admin-bookings">
              {bookings.map((b, i) => (
                <div
                  key={b.id || i}
                  data-testid={`admin-booking-${i}`}
                  className={`rounded-2xl border p-4 transition-colors duration-200 ${
                    b.status === "annullata"
                      ? "border-red-500/20 bg-zinc-900/50 opacity-60"
                      : "border-white/10 bg-zinc-900"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-extrabold text-white">{b.nome}</p>
                    <div className="flex items-center gap-2">
                      {b.payment?.status === "segnalato" && (
                        <button
                          data-testid={`admin-pay-confirm-${i}`}
                          onClick={() => confirmPayment(b.id)}
                          title="Il cliente dice di aver pagato — tocca per confermare"
                          className="rounded-full bg-amber-400/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-400/25"
                        >
                          💳 Da verificare
                        </button>
                      )}
                      {b.payment?.status === "pagato" && (
                        <span
                          data-testid={`admin-pay-ok-${i}`}
                          className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-400"
                        >
                          Pagato ✓
                        </span>
                      )}
                      {b.payment?.status === "in_salone" && (
                        <span
                          data-testid={`admin-pay-salone-${i}`}
                          className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400"
                        >
                          In salone 🤝
                        </span>
                      )}
                      <span
                        data-testid={`admin-status-${i}`}
                        className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_CLS[b.status] || STATUS_CLS.richiesta}`}
                      >
                        {b.status}
                      </span>
                      <button
                        data-testid={`admin-delete-${i}`}
                        onClick={() => deleteBooking(b.id)}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors duration-200 hover:border-red-500/40 hover:text-red-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 flex items-center gap-2 text-sm text-zinc-300">
                    <CalendarCheck size={14} className="text-amber-400" />
                    {b.servizio} — {b.giorno} alle {b.ora}
                    {b.barbiere && <span className="text-zinc-500">· con {b.barbiere}</span>}
                  </p>
                  {b.telefono && (
                    <p className="mt-1 text-xs text-zinc-500">Tel: {b.telefono}</p>
                  )}
                  {b.assenze > 0 && (
                    <p
                      data-testid={`admin-assenze-${i}`}
                      className="mt-1 text-[11px] font-bold text-orange-400"
                    >
                      Questo cliente ha {b.assenze} {b.assenze === 1 ? "assenza" : "assenze"}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-zinc-600">Ricevuta: {fmt(b.created_at)}</p>
                  {b.status === "confermata" && b.telefono && (
                    <div className="mt-3 flex gap-2">
                      <a
                        data-testid={`admin-notify-${i}`}
                        href={notifyClient(b).web}
                        onClick={openExternal(notifyClient(b).web, notifyClient(b).intent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#25D366]/15 py-2 text-xs font-extrabold uppercase tracking-widest text-[#25D366] transition-colors duration-200 hover:bg-[#25D366]/25"
                      >
                        <FaWhatsapp size={14} /> Avvisa cliente
                      </a>
                      <a
                        data-testid={`admin-remind-${i}`}
                        href={reminderClient(b).web}
                        onClick={openExternal(reminderClient(b).web, reminderClient(b).intent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-400/15 py-2 text-xs font-extrabold uppercase tracking-widest text-amber-300 transition-colors duration-200 hover:bg-amber-400/25"
                      >
                        <Bell size={14} /> Promemoria
                      </a>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    {b.status === "confermata" && (
                      <button
                        data-testid={`admin-noshow-${i}`}
                        onClick={() => setStatus(b.id, "assente")}
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-orange-500/15 py-2 text-xs font-extrabold uppercase tracking-widest text-orange-400 transition-colors duration-200 hover:bg-orange-500/25"
                      >
                        Non venuto
                      </button>
                    )}
                    {b.status !== "confermata" && (
                      <button
                        data-testid={`admin-confirm-${i}`}
                        onClick={() => setStatus(b.id, "confermata")}
                        className="flex-1 rounded-xl bg-emerald-500/15 py-2 text-xs font-extrabold uppercase tracking-widest text-emerald-400 transition-colors duration-200 hover:bg-emerald-500/25"
                      >
                        Conferma
                      </button>
                    )}
                    {b.status !== "annullata" && (
                      <button
                        data-testid={`admin-cancel-${i}`}
                        onClick={() => setStatus(b.id, "annullata")}
                        className="flex-1 rounded-xl bg-red-500/10 py-2 text-xs font-extrabold uppercase tracking-widest text-red-400 transition-colors duration-200 hover:bg-red-500/20"
                      >
                        Annulla
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal confirmación eliminar barbero (window.confirm no funciona en PWA) */}
      {tenantToDelete && (
        <div
          data-testid="tenant-delete-modal"
          onClick={() => setTenantToDelete(null)}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-zinc-950/80 p-6 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-3xl border border-red-500/30 bg-zinc-900 p-6 shadow-2xl"
          >
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15 text-red-400">
              <Trash2 size={22} />
            </div>
            <h3 className="mb-1 font-display text-lg font-bold text-white">
              Eliminare l&apos;app di {tenantToDelete.salon_name}?
            </h3>
            <p className="mb-5 text-xs leading-relaxed text-zinc-400">
              La sua app smetterà di funzionare e tutte le sue prenotazioni saranno cancellate.
              Questa azione non si può annullare.
            </p>
            <div className="flex gap-2">
              <button
                data-testid="tenant-delete-cancel"
                onClick={() => setTenantToDelete(null)}
                className="flex-1 rounded-xl border border-white/15 py-3 text-[11px] font-extrabold uppercase tracking-widest text-zinc-300 transition-colors hover:bg-white/5"
              >
                Annulla
              </button>
              <button
                data-testid="tenant-delete-confirm"
                onClick={deleteTenant}
                className="flex-1 rounded-xl bg-red-500 py-3 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-red-400"
              >
                Sì, elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
